import React from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Stack, Box } from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  isEmpty,
  startCase,
  map,
  some,
  sum,
  uniq,
  isString,
  find,
  split,
  filter,
  flatten,
} from 'lodash';

import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { toTitleCase } from 'utils/string';

import { MetadataGridFieldProps, MetaGridField } from './MetaGridField';

// meta field components which can be reused for different resources

export const Name = r =>
  MetaField(
    'Name',
    r?.name ?? r?.identifier?.name ?? r?.identifier?.Name ?? '-',
  );
export const Description = r =>
  MetaField(
    'Description',
    r?.description ||
      r?.config?.Description ||
      r?.config?.description ||
      r?.config?.KeyMetadata?.Description,
    { colSpan: 2 },
  );
export const DescriptionWith1ColSpan = r =>
  MetaField(
    'Description',
    r?.config?.profile?.description ||
      r?.config?.description ||
      r?.config?.Description,
  );
export const AccountID = r => {
  const { toCloudAccountId } = useCloudAccountId();
  return MetaField('Account ID', toCloudAccountId(r?.account));
};
export const AccountLocator = r => {
  const { toCloudAccountId } = useCloudAccountId();
  return MetaField('Account Locator', toCloudAccountId(r?.account));
};
export const CloudID = (r, colSpan = 2) =>
  MetaField('Cloud ID', r?.resource_id, { colSpan: colSpan });
export const ARN = r =>
  MetaField(
    'ARN',
    r?.arn ??
      r?.identifier?.containerInstanceArn ??
      r?.identifier?.clusterArn ??
      r?.identifier?.CertificateArn ??
      r?.identifier?.Arn ??
      r?.identifier?.taskDefinitionArn,
    { colSpan: 2 },
  );
export const Region = r => MetaField('Region', r?.region);
export const VPCId = r =>
  MetaField(
    'VPC ID',
    r?.vpc ||
      r?.vpc_id ||
      r?.connection?.SuperNet ||
      r?.network?.VpcId ||
      r?.network?.DBSubnetGroup?.VpcId ||
      r?.network?.DBSubnetGroup ||
      r?.identifier?.VpcId ||
      r?.identifier?.VPCId ||
      r?.connection?.VirtualPrivateCloud ||
      map(r?.network?.VpcConfig, e => e.VpcId) ||
      map(r?.network?.TransitGatewayVpcAttachments, e => e.VpcId),
    { linkResourceType: NativeResources.VirtualPrivateCloud },
  );
export const CreatedAt = (r, time) =>
  MetaField(
    'Created at',
    isString(r)
      ? r
      : r?.created_at ||
          r?.createdAt ||
          r?.config?.createdAt ||
          r?.config?.creationTime ||
          r?.config?.created_on ||
          r?.config?.createdDate ||
          r?.config?.InstanceCreateTime ||
          r?.config?.StartTime ||
          r?.config?.CreateDate ||
          r?.config?.CreateTime ||
          r?.config?.CreatedTime ||
          r?.config?.CreationTime ||
          r?.config?.CreationDate ||
          r?.config?.CreatedDate ||
          r?.config?.CreationDateTime ||
          r?.config?.LaunchTime ||
          r?.config?.SnapshotCreateTime ||
          r?.config?.KeyMetadata?.CreationDate ||
          r?.config?.Attachment?.AttachTime ||
          r?.config?.Status?.Timeline?.CreationDateTime ||
          r?.config?.CreationTime ||
          r?.config?.created ||
          r?.config?.createdDateTime ||
          r?.config?.CreatedTimestamp ||
          r?.config?.ClusterCreateTime ||
          r?.config?.JoinedTimestamp ||
          r?.config?.registeredAt,
    isString(r) ? {} : { time },
  );
export const LastUpdatedAtUnix = r =>
  MetaField('Last updated at', r?.config?.LastModifiedTimestamp, {
    time: 'unix',
  });
export const LastUpdatedAt = r =>
  MetaField(
    'Last updated at',
    r?.last_activity ||
      r?.config?.lastUpdated ||
      r?.config?.renewedDateTime ||
      r?.config?.lastPasswordChangeDateTime,
    { time: 'utc' },
  );
export const CreatedBy = r => MetaField('Created by', r?.created_by);
export const State = r =>
  MetaField(
    'State',
    isString(r)
      ? r
      : toTitleCase(
          r?.state ||
            r?.status ||
            r?.config?.State?.Name ||
            r?.config?.State?.Code ||
            r?.config?.TableStatus ||
            r?.config?.State ||
            r?.config?.state ||
            r?.config?.ClusterStatus ||
            r?.config?.Status?.State ||
            r?.config?.Status ||
            r?.config?.status ||
            r?.config?.agentStatus ||
            r?.compute?.status ||
            r?.config?.NotebookInstanceStatus ||
            r?.config?.DBInstanceStatus ||
            r?.config?.OrganizationAccountDetails?.Status ||
            r?.config?.KeyMetadata?.KeyState ||
            r?.config?.ConfigRuleState ||
            r?.config?.LifeCycleState,
        ),
  );
export const Status = r =>
  MetaField('Status', r?.config?.Status || r?.config?.status);

/*export const HasInlinePolicies = r =>
  MetaField('Inline policies', r?.has_inline_policy);*/
export const InlinePolicies = r =>
  MetaField(
    'Inline policies',
    r?.config?.GroupInlinePolicies?.map(o => o?.PolicyName),
  );
export const MonitoringEnabled = r =>
  MetaField(
    'Monitoring enabled',
    r?.config?.Monitoring?.State || r?.monitoring,
  );
export const AvailabilityZone = r =>
  MetaField(
    'Availability zone',
    isString(r)
      ? r
      : r?.compute?.Ec2InstanceAttributes?.Ec2AvailabilityZone ||
          r?.config?.AvailabilityZone ||
          r?.config?.availabilityZone ||
          r?.config?.Placement?.AvailabilityZone ||
          r?.network?.VPCOptions?.AvailabilityZones ||
          r?.config?.AvailabilityZones?.map(o => o?.ZoneName ?? o),
  );

// VirtualMachine metadata fields
export const SubnetID = r =>
  MetaField('Subnet ID', r?.connection?.Subnet || r?.network?.SubnetId, {
    linkResourceType: NativeResources.Subnet,
  });
export const SubnetIDs = r =>
  MetaField('Subnet IDs', r?.connection?.Subnet, {
    linkResourceType: NativeResources.Subnet,
  });
export const PublicIP = r =>
  MetaField(
    'Public IP',
    r?.public_ip_address ||
      r?.publicIpAddress ||
      r?.config?.Association?.PublicIp,
  );
export const PrivateIP = r =>
  MetaField('Private IP', r?.network?.PrivateIpAddress);
export const PrivateDNS = r =>
  MetaField('Private DNS', r?.network?.PrivateDnsName);
export const ExternalExposure = r =>
  MetaField('External exposure', r?.external_access);
export const SecurityGroups = r =>
  MetaField(
    'Security groups',
    r?.connection?.SecurityGroup ||
      r?.security?.SecurityGroups ||
      r?.network?.SecurityGroups,
    {
      linkResourceType: NativeResources.SecurityGroup,
    },
  );
export const VMLoggingEnabled = r =>
  MetaField('Logging enabled', r?.config?.Logging?.State || 'false');
export const OpenPorts = r =>
  MetaField('Open ports', r?.security?.SecurityGroups?.length);
export const Interfaces = r =>
  MetaField('Interfaces', r?.connection?.Interface?.length);
export const CrossAccountAccess = r =>
  MetaField('Cross account access', r?.cross_account_access);
/*export const RolesAssigned = r => MetaField('Roles assigned', '~');
export const UsersWithAccess = r => MetaField('Users with access', '~');
export const EBSDisks = r =>
  MetaField('EBS disks', r?.connection?.BlockStorage?.length);*/
export const KeyName = r =>
  MetaField(
    'Key name',
    r?.compute?.Ec2InstanceAttributes?.Ec2KeyName || r?.config?.KeyName,
  );
export const KeyPair = r => MetaField('Key pair', r?.config?.KeyName);
export const ImageID = r => MetaField('Image ID', r?.image);
export const Hypervisor = r => MetaField('Hypervisor', r?.compute?.Hypervisor);
export const IAMInstanceProfileARN = r =>
  MetaField('IAM instance profile ARN', r?.config?.IamInstanceProfile?.Arn);
export const InstanceProfileID = r =>
  MetaField('Instance profile ID', r?.config?.IamInstanceProfile?.Id);
export const InstanceLifecycle = r =>
  MetaField('Instance lifecycle', r?.compute?.InstanceLifecycle);
export const EBSOptimized = r =>
  MetaField('EBS optimized', r?.storage?.EbsOptimized);
export const EBSDisks = r =>
  MetaField(
    'EBS Disks',
    map(r?.ebs_disks, o => o?.volume_id),
    { linkResourceType: NativeResources.ElasticBlockStorage },
  );
export const PublicDNS = r =>
  MetaField('Public DNS', r?.network?.PublicDnsName);
export const GPUID = r =>
  MetaField(
    'GPU ID',
    r?.config?.ElasticGpuAssociations?.map(o => o?.ElasticGpuId),
  );
export const AMIProductCode = r =>
  MetaField(
    'AMI product code',
    r?.config?.ProductCodes?.map(o => o?.ProductCodeId),
  );
export const AMIProductType = r =>
  MetaField(
    'AMI product type',
    uniq(r?.config?.ProductCodes?.map(o => o?.ProductCodeType)),
  );
export const RAMDiskID = r =>
  MetaField('RAM disk ID', r?.identifier?.RamdiskId);
export const VirtualizationType = r =>
  MetaField('Virtualization type', r?.compute?.VirtualizationType);
export const CoreCount = r =>
  MetaField('Core count', r?.compute?.CpuOptions?.CoreCount);
export const ThreadsPerCore = r =>
  MetaField('Threads per core', r?.compute?.CpuOptions?.ThreadsPerCore);
export const RootDeviceName = r =>
  MetaField(
    'Root device name',
    r?.storage?.RootDeviceName ??
      map(r?.config?.Attachments, o => o?.InstanceId + ': ' + o?.Device)?.join(
        ', ',
      ),
  );
export const EBSDeleteOnTermination = r =>
  MetaField(
    'Delete on termination',
    map(
      r?.config?.Attachments,
      o => o?.InstanceId + ': ' + o?.DeleteOnTermination,
    ),
  );
export const InstanceType = r =>
  MetaField('Instance type', r?.instance_type || r?.instanceType);
export const EBSVpcID = r => MetaField('VPC ID', r?.vpc || r?.vpc_id);
export const TerminationProtection = r =>
  MetaField('Termination protection', r?.disable_api_termination);
export const DeleteOnTermination = r =>
  MetaField(
    'Delete on termination',
    map(
      r?.storage?.BlockDeviceMappings,
      o => o?.Ebs?.VolumeId + ': ' + o?.Ebs?.DeleteOnTermination,
    ),
  );
export const HttpTokens = r =>
  MetaField('HTTP tokens', r?.config?.MetadataOptions?.HttpTokens);
export const HttpPutHopLimit = r =>
  MetaField(
    'HTTP put hop limit',
    r?.config?.MetadataOptions?.HttpPutResponseHopLimit,
  );
export const HttpEndpoint = r =>
  MetaField('HTTP endpoint', r?.config?.MetadataOptions?.HttpEndpoint);

// SecurityGroup metadata
export const Rules = r =>
  MetaField(
    'Rules',
    r?.network?.Rules ??
      (r?.network?.IpPermissions?.length ?? 0) +
        (r?.network?.IpPermissionsEgress?.length ?? 0),
  );
export const Ingress = r =>
  MetaField('Ingress CIDR 0.0.0.0/0', r?.network?.IngressCIDR);
export const Egress = r =>
  MetaField('Egress CIDR 0.0.0.0/0', r?.network?.EgressCIDR);
export const NetworkInterfaceIDs = r =>
  MetaField('Network interface IDs', r?.network_interface_ids, {
    linkResourceType: NativeResources.ElasticNetworkInterface,
  });

//Gitlab
export const Visibility = r => MetaField('Visibility', r?.security?.visibility);
export const Subgroups = r =>
  MetaField(
    'Shared groups',
    r?.config?.shared_with_groups?.map(
      o => o?.group_full_path + ' : ' + o?.group_access_level,
    ),
  );
export const Projects = r => MetaField('Projects', '~');
export const AccessProtocols = r => MetaField('Access protocols', '~');
export const Webhooks = r =>
  MetaField(
    'Webhooks',
    r?.network?.webhooks?.map(o => o?.hook_url),
  );
export const CICDRunners = r =>
  MetaField('CI/CD Runners', r?.config?.ci_cd_runners ? 'Enabled' : 'Disabled');
export const VariableKey = r => MetaField('Key', r?.config?.variables?.key);
export const VariableValue = r =>
  MetaField('Value', r?.config?.variables?.value);
export const VariableAttributes = r =>
  MetaField('Attributes', r?.config?.variables?.attributes);

// Interface metadata
export const InterfaceDeleteOnTermination = r =>
  MetaField(
    'Delete on termination',
    r?.config?.Attachment?.DeleteOnTermination,
  );
export const Owner = r => {
  const name =
    r?.owner ??
    r?.identifier?.Owner ??
    r?.identifier?.OwnerId ??
    r?.identifier?.ownerAccountId ??
    r?.config?.owner;
  return MetaField(
    'Owned by',
    !!name
      ? [
          {
            name,
            id: r?.owner_id ?? r?.identifier?.OwnerId,
          },
        ]
      : [],
    {
      linkResourceType: NativeResources.IamUser,
    },
  );
};
export const IPOwner = r =>
  MetaField('IP owner', r?.config?.Association?.IpOwnerId);
export const AttachedTo = r =>
  MetaField('Attached to', r?.config?.Attachment?.InstanceId);
export const FirewallIDs = r =>
  MetaField(
    'Firewall IDs',
    r?.config?.Groups?.map(o => o?.GroupName),
  );
export const MACAddress = r => MetaField('MAC address', r?.network?.MacAddress);
export const SourceDestinationCheck = r =>
  MetaField('Source destination check', r?.config?.SourceDestCheck);

// NetworkACL metadata
export const AssociatedIDs = r =>
  MetaField(
    'Associated IDs',
    r?.config?.Associations?.map(o => o?.NetworkAclAssociationId),
  );
export const Default = r =>
  MetaField(
    'Default',
    r?.is_default ?? r?.config?.Assoications?.NetworkAclAssociationId,
  );
export const NACLOwner = r => MetaField('Owner', r?.owner_id);

// VPC metadata
export const IPv4CIDRBlock = r =>
  MetaField('IPv4 CIDR block', r?.network?.CidrBlock);
export const IPv4CIDRBlockState = r =>
  MetaField(
    'IPv4 CIDR block state',
    r?.network?.CidrBlockAssociationSet?.[0]?.CidrBlockState?.State,
  );
export const DHCPOptionID = r =>
  MetaField('DHCP options ID', r?.connection?.DHCPOptions, {
    linkResourceType: NativeResources.DhcpOptions,
  });
export const VPCDefault = r => MetaField('Default', r?.config?.IsDefault, {});

// Subnet metadata
export const DefaultForAz = r =>
  MetaField('Default for Az', r?.config?.DefaultForAz);
export const AvailableIPs = r =>
  MetaField('Available IPs', r?.network?.AvailableIpAddressCount);
export const AvailableIPsForIPV6 = r => MetaField('Available IPs', '~');
export const AttachIPv6 = r =>
  MetaField('Attach IPv6', r?.assign_ipv6_address_on_creation);
export const IPv6Native = r => MetaField('IPv6 Native', r?.network?.Ipv6Native);
export const IPv6CIDRBlock = r =>
  MetaField(
    'IPv6 CIDR block state',
    map(
      r?.network?.Ipv6CidrBlockAssociationSet,
      o => o?.Ipv6CidrBlock + ' : ' + o?.Ipv6CidrBlockState?.State,
    ),
  );
export const IPv6CIDRBlockState = r => MetaField('IPv6 CIDR block state', '~');
export const SubnetState = r => MetaField('State', r?.config?.State);

// KMS metadata
export const KeySpec = r =>
  MetaField('Key spec', r?.config?.KeyMetadata?.KeySpec);
export const Aliases = r =>
  MetaField(
    'Aliases',
    r?.config?.Aliases?.map(o => o?.AliasName),
  );
export const KeyUsage = r =>
  MetaField('Key usage', r?.config?.KeyMetadata?.KeyUsage);
export const KeyState = r =>
  MetaField('State', r?.config?.KeyMetadata?.KeyState);
export const KeyManager = r => MetaField('Key manager', r?.key_manager);
export const MultiRegion = r =>
  MetaField(
    'Multi-region',
    r?.config?.KeyMetadata?.MultiRegion ?? r?.config?.IsMultiRegionTrail,
  );
export const KeyRotation = r =>
  MetaField('Key rotation', r?.config?.KeyRotationEnabled);
export const RotationPeriod = r =>
  MetaField(
    'Rotation period',
    r?.config?.KeyRotatio || r?.config?.RotationRules?.Duration,
  );

// EBS metadata
export const Encrypted = r =>
  MetaField(
    'Encrypted',
    r?.encrypted ?? r?.security?.Encrypted ?? r?.storage?.StorageEncrypted,
  );
export const KeyID = r =>
  MetaField('Key ID', r?.identifier?.KmsKeyId || r?.connection?.Credential);
export const Size = r => MetaField('Size', r?.storage?.Size);
export const IOPS = r => MetaField('IOPS', r?.storage?.Iops ?? r?.config?.Iops);
export const Type = r =>
  MetaField(
    'Type',
    r?.storage?.VolumeType ||
      r?.config?.groupType ||
      r?.config?.type ||
      r?.config?.KeyType ||
      r?.config?.kind ||
      r?.config?.AccountType ||
      r?.config?.HealthCheckConfig?.Type,
  );
export const MultiAttachAllowed = r =>
  MetaField('Multi-attach Allowed', r?.storage?.MultiAttachEnabled);
export const VirtualMachines = r =>
  MetaField('Virtual machines', r?.connection?.EC2Instance, {
    linkResourceType: NativeResources.Ec2Instance,
  });

// S3 metadata
export const Grants = r =>
  MetaField(
    'Grants',
    map(r?.config?.Grants, g => `${g?.Grantee?.ID}/${g?.Permission}`),
    { colSpan: 2 },
  );
export const ServerSideEncryption = r =>
  MetaField(
    'Server-side encryption',
    r?.security?.ServerSideEncryptionConfiguration?.Rules?.map(
      a => a?.ApplyServerSideEncryptionByDefault?.SSEAlgorithm,
    ),
  );
export const BucketKeyEnabled = r =>
  MetaField(
    'Bucket key enabled',
    r?.security?.ServerSideEncryptionConfiguration?.Rules?.map(a =>
      startCase(a?.BucketKeyEnabled),
    ),
  );
export const VersioningEnabled = r =>
  MetaField(
    'Versioning enabled',
    r?.config?.Versioning?.Status === 'Enabled' ? 'Yes' : 'No',
  );
export const WebsiteEnabled = r =>
  MetaField('Website enabled', !r?.website ? 'No' : 'Yes');
export const BlockPublicACLs = r =>
  MetaField(
    'Block public ACLs',
    r?.config?.PublicAccessBlockConfiguration?.BlockPublicAcls,
  );
export const IgnorePublicACLs = r =>
  MetaField(
    'Ignore public ACLs',
    r?.config?.PublicAccessBlockConfiguration?.IgnorePublicAcls,
  );
export const BlockPublicPolicy = r =>
  MetaField(
    'Block public policy',
    r?.config?.PublicAccessBlockConfiguration?.BlockPublicPolicy,
  );
export const RestrictPublicBuckets = r =>
  MetaField(
    'Restrict public buckets',
    r?.config?.PublicAccessBlockConfiguration?.RestrictPublicBuckets,
  );
export const Policy = r => MetaField('Resource policy', r?.has_resource_policy);
export const S3State = r => MetaField('State', 'Active');

// EFS metadata
export const EncryptionKey = r =>
  MetaField(
    'Encryption key',
    r?.connection?.Credential ?? r?.identifier?.KmsKeyId,
    { colSpan: 2 },
  );
export const LifecycleState = r =>
  MetaField('Lifecycle state', r?.config?.LifeCycleState);
export const DataInInfrequentAccess = r =>
  MetaField(
    'Data in infrequent access (ValueInIA)',
    r?.storage?.SizeInBytes?.ValueInIA,
  );
export const DataInStandard = r =>
  MetaField(
    'Data in standard (ValueInStandard)',
    r?.storage?.SizeInBytes?.ValueInStandard,
  );
export const PerformanceMode = r =>
  MetaField('Performance mode', r?.config?.PerformanceMode);
export const ThroughputMode = r =>
  MetaField('Throughput mode', r?.config?.ThroughputMode);
export const MountTargets = r =>
  MetaField('Mount targets', r?.storage?.NumberOfMountTargets);
export const CreationToken = r =>
  MetaField('Creation token', r?.config?.CreationToken, { colSpan: 2 });
export const EFSSize = r => MetaField('Size', r?.storage?.SizeInBytes?.Value);

// AWS Bedrock knowledge base and S3 vector bucket metadata
export const KnowledgeBaseType = r =>
  MetaField(
    'Knowledge Base Type',
    r?.config?.knowledgeBaseConfiguration?.type ?? r?.type,
  );
export const EmbeddingModelARN = r =>
  MetaField(
    'Embedding Model ARN',
    r?.config?.knowledgeBaseConfiguration?.vectorKnowledgeBaseConfiguration
      ?.embeddingModelArn ?? r?.embeddingModelArn,
    { colSpan: 2 },
  );
export const EmbeddingDimensions = r =>
  MetaField(
    'Embedding Dimensions',
    r?.config?.knowledgeBaseConfiguration?.vectorKnowledgeBaseConfiguration
      ?.embeddingModelConfiguration?.bedrockEmbeddingModelConfiguration
      ?.dimensions ?? r?.dimensions,
  );
export const EmbeddingDataType = r =>
  MetaField(
    'Embedding Data Type',
    r?.config?.knowledgeBaseConfiguration?.vectorKnowledgeBaseConfiguration
      ?.embeddingModelConfiguration?.bedrockEmbeddingModelConfiguration
      ?.embeddingDataType ?? r?.embeddingDataType,
  );
export const VectorIndexARN = r =>
  MetaField(
    'Vector Index ARN',
    r?.config?.storageConfiguration?.s3VectorsConfiguration?.indexArn ??
      r?.indexArn,
    { colSpan: 2 },
  );
export const VectorIndexName = r =>
  MetaField(
    'Vector Index Name',
    r?.config?.storageConfiguration?.s3VectorsConfiguration?.indexName ??
      r?.indexName,
  );
export const VectorBucketARN = r =>
  MetaField(
    'Vector Bucket ARN',
    r?.config?.storageConfiguration?.s3VectorsConfiguration?.vectorBucketArn ??
      r?.vectorBucketArn,
    { colSpan: 2 },
  );
export const IAMRoleARN = r =>
  MetaField(
    'IAM Role ARN',
    r?.roleArn || r?.security?.roleArn || r?.identifier?.roleArn,
    { colSpan: 2 },
  );
export const KnowledgeBaseStorageType = r =>
  MetaField('Storage Type', r?.config?.storageConfiguration?.type);
export const KnowledgeBaseRDSClusterARN = r =>
  MetaField(
    'RDS Cluster ARN',
    r?.config?.storageConfiguration?.rdsConfiguration?.resourceArn,
    { colSpan: 2 },
  );
export const KnowledgeBasePineconeCredentialsSecretARN = r =>
  MetaField(
    'Pinecone Credentials Secret ARN',
    r?.config?.storageConfiguration?.pineconeConfiguration
      ?.credentialsSecretArn,
    { colSpan: 2 },
  );
export const KnowledgeBaseRedisCredentialsSecretARN = r =>
  MetaField(
    'Redis Credentials Secret ARN',
    r?.config?.storageConfiguration?.redisEnterpriseCloudConfiguration
      ?.credentialsSecretArn,
    { colSpan: 2 },
  );
export const KnowledgeBaseMongoDBCredentialsSecretARN = r =>
  MetaField(
    'MongoDB Atlas Credentials Secret ARN',
    r?.config?.storageConfiguration?.mongoDbAtlasConfiguration
      ?.credentialsSecretArn,
    { colSpan: 2 },
  );
export const VectorIndexDataType = r =>
  MetaField('Index Data Type', r?.dataType);
export const VectorIndexDimension = r =>
  MetaField('Index Dimension', r?.dimension);
export const VectorIndexDistanceMetric = r =>
  MetaField('Index Distance Metric', r?.distanceMetric);
export const VectorIndexNonFilterableMetadataKeys = r =>
  MetaField('Index Non-Filterable Metadata Keys', r?.nonFilterableMetadataKeys);
export const VectorIndexEncryptionType = r =>
  MetaField('Index Encryption Type', r?.sseType);
export const VectorIndexKMSKeyARN = r =>
  MetaField('Index KMS Key ARN', r?.kmsKeyArn);
export const VectorBucketEncryptionType = r =>
  MetaField('Encryption Type', r?.encryption_type);
export const VectorBucketKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.kmsKeyArn);

// S3 vector bucket metadata
export const VectorBucketIndexNames = r =>
  MetaField(
    'Index Name',
    r?.config?.indexes?.map(index => index?.indexName),
  );
export const VectorBucketIndexARNs = r =>
  MetaField(
    'Index ARN',
    r?.config?.indexes?.map(index => index?.indexArn),
    { colSpan: 2 },
  );
export const VectorBucketIndexDataTypes = r =>
  MetaField(
    'Index Data Type',
    r?.config?.indexes?.map(index => index?.dataType),
  );
export const VectorBucketIndexDimensions = r =>
  MetaField(
    'Index Dimension',
    r?.config?.indexes?.map(index => index?.dimension),
  );
export const VectorBucketIndexDistanceMetrics = r =>
  MetaField(
    'Index Distance Metric',
    r?.config?.indexes?.map(index => index?.distanceMetric),
  );
export const VectorBucketIndexNonFilterableMetadataKeys = r =>
  MetaField(
    'Index Non-Filterable Metadata Keys',
    r?.config?.indexes?.flatMap(
      index => index?.metadataConfiguration?.nonFilterableMetadataKeys ?? [],
    ),
  );
export const VectorBucketIndexEncryptionTypes = r =>
  MetaField(
    'Index Encryption Type',
    r?.config?.indexes?.map(index => index?.encryptionConfiguration?.sseType),
  );
export const VectorBucketIndexKMSKeyARNs = r =>
  MetaField(
    'Index KMS Key ARN',
    r?.config?.indexes?.map(index => index?.encryptionConfiguration?.kmsKeyArn),
    { colSpan: 2 },
  );
export const VectorBucketName = r =>
  MetaField('Vector Bucket Name', r?.identifier?.vectorBucketName);
export const VectorBucketIdentifierARN = r =>
  MetaField('Vector Bucket ARN', r?.identifier?.vectorBucketArn, {
    colSpan: 2,
  });
export const VectorBucketSecurityEncryptionType = r =>
  MetaField('Encryption Type', r?.security?.encryptionConfiguration?.sseType);
export const VectorBucketSecurityKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.encryptionConfiguration?.kmsKeyArn, {
    colSpan: 2,
  });

// AWS Bedrock Agent metadata
export const BedrockAgentFoundationModel = r =>
  MetaField('Foundation Model', r?.config?.foundationModel);
export const BedrockAgentGuardrailIdentifier = r =>
  MetaField(
    'Guardrail Identifier',
    r?.config?.guardrailConfiguration?.guardrailIdentifier,
  );
export const BedrockAgentGuardrailVersion = r =>
  MetaField(
    'Guardrail Version',
    r?.config?.guardrailConfiguration?.guardrailVersion,
  );
export const BedrockAgentKnowledgeBases = r =>
  MetaField(
    'Associated Knowledge Bases',
    r?.config?.knowledgeBases?.map(
      knowledgeBase => knowledgeBase?.knowledgeBaseId,
    ),
  );
export const BedrockAgentActionGroupNames = r =>
  MetaField(
    'Action Group Name',
    r?.config?.actionGroups?.map(actionGroup => actionGroup?.actionGroupName),
  );
export const BedrockAgentActionGroupStates = r =>
  MetaField(
    'Action Group State',
    r?.config?.actionGroups?.map(actionGroup => actionGroup?.actionGroupState),
  );
export const BedrockAgentActionGroupExecutorLambdaARNs = r =>
  MetaField(
    'Action Group Executor Lambda ARN',
    r?.config?.actionGroups?.map(
      actionGroup => actionGroup?.actionGroupExecutor?.lambda,
    ),
    { colSpan: 2 },
  );
export const BedrockAgentAliasNames = r =>
  MetaField(
    'Alias Names',
    r?.config?.agentAliases?.map(agentAlias => agentAlias?.agentAliasName),
  );
export const BedrockAgentIAMRoleARN = r =>
  MetaField('IAM Role ARN', r?.security?.agentResourceRoleArn, { colSpan: 2 });
export const BedrockAgentKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.customerEncryptionKeyArn, {
    colSpan: 2,
  });

// AWS Bedrock Flow metadata
const bedrockFlowNodeValues = (r, getValue) =>
  r?.config?.definition?.nodes?.map(getValue).filter(Boolean);

export const BedrockFlowAgentAliasNodes = r =>
  MetaField(
    'Agent Alias Nodes',
    bedrockFlowNodeValues(r, node => node?.configuration?.agent?.agentAliasArn),
  );
export const BedrockFlowKnowledgeBaseNodes = r =>
  MetaField(
    'Knowledge Base Nodes',
    bedrockFlowNodeValues(
      r,
      node => node?.configuration?.knowledgeBase?.knowledgeBaseId,
    ),
  );
export const BedrockFlowLambdaFunctionNodes = r =>
  MetaField(
    'Lambda Function Nodes',
    bedrockFlowNodeValues(
      r,
      node => node?.configuration?.lambdaFunction?.lambdaArn,
    ),
  );
export const BedrockFlowPromptNodes = r =>
  MetaField(
    'Prompt Nodes',
    bedrockFlowNodeValues(
      r,
      node =>
        node?.configuration?.prompt?.sourceConfiguration?.resource?.promptArn,
    ),
  );
export const BedrockFlowPromptNodeGuardrails = r =>
  MetaField(
    'Prompt Node Guardrails',
    bedrockFlowNodeValues(
      r,
      node =>
        node?.configuration?.prompt?.guardrailConfiguration
          ?.guardrailIdentifier,
    ),
  );
export const BedrockFlowStorageNodeS3Buckets = r =>
  MetaField(
    'Storage Node S3 Buckets',
    bedrockFlowNodeValues(
      r,
      node =>
        node?.configuration?.storage?.serviceConfiguration?.s3?.bucketName,
    ),
  );
export const BedrockFlowRetrievalNodeS3Buckets = r =>
  MetaField(
    'Retrieval Node S3 Buckets',
    bedrockFlowNodeValues(
      r,
      node =>
        node?.configuration?.retrieval?.serviceConfiguration?.s3?.bucketName,
    ),
  );
export const BedrockFlowExecutionRoleARN = r =>
  MetaField('Execution Role ARN', r?.security?.executionRoleArn, {
    colSpan: 2,
  });
export const BedrockFlowKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.customerEncryptionKeyArn, {
    colSpan: 2,
  });

// AWS Bedrock AgentCore Memory metadata
export const AgentCoreMemoryEventExpiryDuration = r =>
  MetaField('Event Expiry (Days)', r?.config?.eventExpiryDuration);
export const AgentCoreMemoryManagedByResourceARN = r =>
  MetaField('Managed By Resource ARN', r?.config?.managedByResourceArn, {
    colSpan: 2,
  });
export const AgentCoreMemoryStrategyNames = r =>
  MetaField(
    'Strategy Name',
    r?.config?.strategies?.map(strategy => strategy?.name),
  );
export const AgentCoreMemoryStrategyTypes = r =>
  MetaField(
    'Strategy Type',
    r?.config?.strategies?.map(strategy => strategy?.type),
  );
export const AgentCoreMemoryStrategyStatuses = r =>
  MetaField(
    'Strategy Status',
    r?.config?.strategies?.map(strategy => strategy?.status),
  );
export const AgentCoreMemoryStrategyNamespaceTemplates = r =>
  MetaField(
    'Strategy Namespace Templates',
    r?.config?.strategies?.flatMap(
      strategy => strategy?.namespaceTemplates ?? [],
    ),
  );
export const AgentCoreMemoryIndexedMetadataKeys = r =>
  MetaField(
    'Indexed Metadata Keys',
    r?.config?.indexedKeys?.map(indexedKey => indexedKey?.key),
  );
export const AgentCoreMemoryKinesisStreamARN = r =>
  MetaField(
    'Stream Delivery Kinesis Stream ARN',
    r?.config?.streamDeliveryResources?.resources?.[0]?.kinesis?.dataStreamArn,
    { colSpan: 2 },
  );
export const AgentCoreMemoryKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.encryptionKeyArn, { colSpan: 2 });
export const AgentCoreMemoryIAMRoleARN = r =>
  MetaField('IAM Role ARN', r?.security?.memoryExecutionRoleArn, {
    colSpan: 2,
  });

// AWS Bedrock Knowledge Base Data Source metadata
export const BedrockDataSourceType = r =>
  MetaField('Data Source Type', r?.config?.dataSourceConfiguration?.type);
export const BedrockDataSourceS3BucketARN = r =>
  MetaField(
    'S3 Bucket ARN',
    r?.config?.dataSourceConfiguration?.s3Configuration?.bucketArn,
    { colSpan: 2 },
  );
export const BedrockDataSourceS3InclusionPrefixes = r =>
  MetaField(
    'S3 Inclusion Prefixes',
    r?.config?.dataSourceConfiguration?.s3Configuration?.inclusionPrefixes,
  );
export const BedrockDataSourceConfluenceSecretARN = r =>
  MetaField(
    'Confluence Credentials Secret ARN',
    r?.config?.dataSourceConfiguration?.confluenceConfiguration
      ?.sourceConfiguration?.credentialsSecretArn,
    { colSpan: 2 },
  );
export const BedrockDataSourceSalesforceSecretARN = r =>
  MetaField(
    'Salesforce Credentials Secret ARN',
    r?.config?.dataSourceConfiguration?.salesforceConfiguration
      ?.sourceConfiguration?.credentialsSecretArn,
    { colSpan: 2 },
  );
export const BedrockDataSourceSharePointSecretARN = r =>
  MetaField(
    'SharePoint Credentials Secret ARN',
    r?.config?.dataSourceConfiguration?.sharePointConfiguration
      ?.sourceConfiguration?.credentialsSecretArn,
    { colSpan: 2 },
  );
export const BedrockDataSourceChunkingStrategy = r =>
  MetaField(
    'Chunking Strategy',
    r?.config?.vectorIngestionConfiguration?.chunkingConfiguration
      ?.chunkingStrategy,
  );
export const BedrockDataSourceParsingModelARN = r =>
  MetaField(
    'Parsing Foundation Model ARN',
    r?.config?.vectorIngestionConfiguration?.parsingConfiguration
      ?.bedrockFoundationModelConfiguration?.modelArn,
    { colSpan: 2 },
  );
export const BedrockDataSourceKnowledgeBaseID = r =>
  MetaField('Knowledge Base ID', r?.identifier?.knowledgeBaseId);

// AWS Bedrock Inference Profile metadata
export const BedrockInferenceProfileType = r =>
  MetaField('Type', r?.config?.type);
export const BedrockInferenceProfileModelARNs = r =>
  MetaField(
    'Underlying Model ARNs',
    r?.config?.models?.map(model => model?.modelArn),
  );

// AWS Bedrock Custom Model metadata
export const BedrockCustomModelTrainingDataBucket = r =>
  MetaField('Training Data Bucket', r?.config?.trainingDataConfig?.s3Uri, {
    colSpan: 2,
  });
export const BedrockCustomModelOutputDataBucket = r =>
  MetaField('Output Data Bucket', r?.config?.outputDataConfig?.s3OutputPath, {
    colSpan: 2,
  });
export const BedrockCustomModelDeploymentARNs = r =>
  MetaField(
    'Deployment ARN',
    r?.config?.deployments?.map(
      deployment => deployment?.customModelDeploymentArn,
    ),
    { colSpan: 2 },
  );
export const BedrockCustomModelDeploymentNames = r =>
  MetaField(
    'Deployment Name',
    r?.config?.deployments?.map(deployment => deployment?.modelDeploymentName),
  );
export const BedrockCustomModelDeploymentStatuses = r =>
  MetaField(
    'Deployment Status',
    r?.config?.deployments?.map(deployment => deployment?.status),
  );
export const BedrockModelARN = r =>
  MetaField('Model ARN', r?.identifier?.modelArn, { colSpan: 2 });
export const BedrockCustomModelBaseModelARN = r =>
  MetaField('Base Model ARN', r?.identifier?.baseModelArn, { colSpan: 2 });
export const BedrockCustomModelBaseModelName = r =>
  MetaField('Base Model Name', r?.identifier?.baseModelName);
export const BedrockCustomModelOwnerAccountID = r =>
  MetaField('Owner Account ID', r?.identifier?.ownerAccountId);
export const BedrockModelKMSKeyARN = r =>
  MetaField('Model KMS Key ARN', r?.identifier?.modelKmsKeyArn, {
    colSpan: 2,
  });

// AWS Bedrock Foundation Model metadata
export const BedrockFoundationModelInputModalities = r =>
  MetaField('Input Modalities', r?.config?.inputModalities);
export const BedrockFoundationModelOutputModalities = r =>
  MetaField('Output Modalities', r?.config?.outputModalities);
export const BedrockFoundationModelLifecycleStatus = r =>
  MetaField('Lifecycle Status', r?.config?.modelLifecycle?.status);
export const BedrockFoundationModelProvider = r =>
  MetaField('Provider', r?.identifier?.providerName);

// AWS Bedrock Imported Model metadata
export const BedrockImportedModelDataSource = r =>
  MetaField(
    'Model Data Source',
    r?.config?.modelDataSource?.s3DataSource?.s3Uri,
    { colSpan: 2 },
  );
export const BedrockImportedModelArchitecture = r =>
  MetaField('Model Architecture', r?.config?.modelArchitecture);

// AWS Bedrock AgentCore workspace metadata
export const AgentCoreBrowserSigningEnabled = r =>
  MetaField('Browser Signing Enabled', r?.config?.browserSigning?.enabled);
export const AgentCoreRecordingEnabled = r =>
  MetaField('Recording Enabled', r?.config?.recording?.enabled);
export const AgentCoreRecordingS3Bucket = r =>
  MetaField('Recording S3 Bucket', r?.config?.recording?.s3Location?.bucket);
export const AgentCoreRecordingS3Prefix = r =>
  MetaField('Recording S3 Prefix', r?.config?.recording?.s3Location?.prefix);
export const AgentCoreWorkspaceNetworkMode = r =>
  MetaField('Network Mode', r?.network?.networkConfiguration?.networkMode);
export const AgentCoreWorkspaceSubnets = r =>
  MetaField('Subnets', r?.network?.networkConfiguration?.vpcConfig?.subnets);
export const AgentCoreWorkspaceSecurityGroups = r =>
  MetaField(
    'Security Groups',
    r?.network?.networkConfiguration?.vpcConfig?.securityGroups,
  );
export const AgentCoreWorkspaceIAMRoleARN = r =>
  MetaField('IAM Role ARN', r?.security?.executionRoleArn, { colSpan: 2 });

// AWS Bedrock Prompt metadata
export const BedrockPromptVariantNames = r =>
  MetaField(
    'Variant Name',
    r?.config?.variants?.map(variant => variant?.name),
  );
export const BedrockPromptVariantTemplateTypes = r =>
  MetaField(
    'Variant Template Type',
    r?.config?.variants?.map(variant => variant?.templateType),
  );
export const BedrockPromptVariantModelIDs = r =>
  MetaField(
    'Variant Model ID',
    r?.config?.variants?.map(variant => variant?.modelId),
  );
export const BedrockPromptKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.customerEncryptionKeyArn, {
    colSpan: 2,
  });

// AWS Bedrock Provisioned Model metadata
export const BedrockProvisionedModelUnits = r =>
  MetaField('Model Units', r?.config?.modelUnits);
export const BedrockProvisionedCommitmentExpirationTime = r =>
  MetaField('Commitment Expiration Time', r?.config?.commitmentExpirationTime, {
    time: 'dayjs',
  });
export const BedrockProvisionedFoundationModelARN = r =>
  MetaField('Foundation Model ARN', r?.identifier?.foundationModelArn, {
    colSpan: 2,
  });

// AWS Bedrock AgentCore Policy metadata
export const AgentCorePolicyEnforcementMode = r =>
  MetaField('Enforcement Mode', r?.config?.enforcementMode);
export const AgentCorePolicyEngineID = r =>
  MetaField('Policy Engine ID', r?.identifier?.policyEngineId);

// AWS Bedrock AgentCore Policy Engine metadata
export const AgentCorePolicyEngineKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.encryptionKeyArn, {
    colSpan: 2,
  });

// AWS Bedrock Guardrail metadata
export const BedrockGuardrailContentFilterTypes = r =>
  MetaField(
    'Content Filter Types',
    r?.config?.contentPolicy?.filters?.map(filter => filter?.type),
  );
export const BedrockGuardrailContentPolicyTier = r =>
  MetaField('Content Policy Tier', r?.config?.contentPolicy?.tier?.tierName);
export const BedrockGuardrailDeniedTopics = r =>
  MetaField(
    'Denied Topics',
    r?.config?.topicPolicy?.topics?.map(topic => topic?.name),
  );
export const BedrockGuardrailManagedWordListTypes = r =>
  MetaField(
    'Managed Word List Types',
    r?.config?.wordPolicy?.managedWordLists?.map(wordList => wordList?.type),
  );
export const BedrockGuardrailPIIEntityTypes = r =>
  MetaField(
    'PII Entity Types',
    r?.config?.sensitiveInformationPolicy?.piiEntities?.map(
      entity => entity?.type,
    ),
  );
export const BedrockGuardrailPIIEntityActions = r =>
  MetaField(
    'PII Entity Actions',
    r?.config?.sensitiveInformationPolicy?.piiEntities?.map(
      entity => entity?.action,
    ),
  );
export const BedrockGuardrailProfileID = r =>
  MetaField(
    'Guardrail Profile ID',
    r?.config?.crossRegionDetails?.guardrailProfileId,
  );
export const BedrockGuardrailProfileARN = r =>
  MetaField(
    'Guardrail Profile ARN',
    r?.config?.crossRegionDetails?.guardrailProfileArn,
    { colSpan: 2 },
  );
export const BedrockGuardrailAccountEnforced = r =>
  MetaField('Account Enforced', r?.config?.accountEnforced);
export const BedrockGuardrailOrganizationEnforced = r =>
  MetaField('Organization Enforced', r?.config?.organizationEnforced);
export const BedrockGuardrailKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.kmsKeyArn, { colSpan: 2 });

// AWS Bedrock Invocation Logging metadata
export const BedrockInvocationTextDataLoggingEnabled = r =>
  MetaField(
    'Text Data Logging Enabled',
    r?.config?.loggingConfig?.textDataDeliveryEnabled,
  );
export const BedrockInvocationImageDataLoggingEnabled = r =>
  MetaField(
    'Image Data Logging Enabled',
    r?.config?.loggingConfig?.imageDataDeliveryEnabled,
  );
export const BedrockInvocationEmbeddingDataLoggingEnabled = r =>
  MetaField(
    'Embedding Data Logging Enabled',
    r?.config?.loggingConfig?.embeddingDataDeliveryEnabled,
  );
export const BedrockInvocationVideoDataLoggingEnabled = r =>
  MetaField(
    'Video Data Logging Enabled',
    r?.config?.loggingConfig?.videoDataDeliveryEnabled,
  );
export const BedrockInvocationAudioDataLoggingEnabled = r =>
  MetaField(
    'Audio Data Logging Enabled',
    r?.config?.loggingConfig?.audioDataDeliveryEnabled,
  );
export const BedrockInvocationS3LogBucket = r =>
  MetaField('S3 Log Bucket', r?.config?.loggingConfig?.s3Config?.bucketName);
export const BedrockInvocationS3LogKeyPrefix = r =>
  MetaField('S3 Log Key Prefix', r?.config?.loggingConfig?.s3Config?.keyPrefix);
export const BedrockInvocationCloudWatchLogGroup = r =>
  MetaField(
    'CloudWatch Log Group',
    r?.config?.loggingConfig?.cloudWatchConfig?.logGroupName,
  );
export const BedrockInvocationCloudWatchDeliveryRoleARN = r =>
  MetaField(
    'CloudWatch Delivery Role ARN',
    r?.config?.loggingConfig?.cloudWatchConfig?.roleArn,
    { colSpan: 2 },
  );
export const BedrockInvocationLargeDataDeliveryS3Bucket = r =>
  MetaField(
    'Large Data Delivery S3 Bucket',
    r?.config?.loggingConfig?.cloudWatchConfig?.largeDataDeliveryS3Config
      ?.bucketName,
  );
export const BedrockInvocationLargeDataDeliveryS3KeyPrefix = r =>
  MetaField(
    'Large Data Delivery S3 Key Prefix',
    r?.config?.loggingConfig?.cloudWatchConfig?.largeDataDeliveryS3Config
      ?.keyPrefix,
  );

// AWS Bedrock AgentCore Runtime metadata
export const AgentCoreRuntimeStatus = r => MetaField('Status', r?.status);
export const AgentCoreRuntimeCreatedAt = r =>
  MetaField('Created At', r?.createdAt, { time: 'dayjs' });
export const AgentCoreRuntimeUpdatedAt = r =>
  MetaField('Updated At', r?.lastUpdatedAt, { time: 'dayjs' });
export const AgentCoreRuntimeVersion = r =>
  MetaField('Version', r?.config?.agentRuntimeVersion);
export const AgentCoreRuntimeServerProtocol = r =>
  MetaField(
    'Server Protocol',
    r?.config?.protocolConfiguration?.serverProtocol,
  );
export const AgentCoreRuntimeIdleSessionTimeout = r =>
  MetaField(
    'Idle Runtime Session Timeout',
    r?.config?.lifecycleConfiguration?.idleRuntimeSessionTimeout,
  );
export const AgentCoreRuntimeMaxLifetime = r =>
  MetaField('Max Lifetime', r?.config?.lifecycleConfiguration?.maxLifetime);
export const AgentCoreRuntimeWorkloadIdentityARN = r =>
  MetaField(
    'Workload Identity ARN',
    r?.config?.workloadIdentityDetails?.workloadIdentityArn,
    { colSpan: 2 },
  );
export const AgentCoreRuntimeNetworkMode = r =>
  MetaField('Network Mode', r?.network?.networkConfiguration?.networkMode);
export const AgentCoreRuntimeSubnets = r =>
  MetaField(
    'Subnets',
    r?.network?.networkConfiguration?.networkModeConfig?.subnets,
  );
export const AgentCoreRuntimeSecurityGroups = r =>
  MetaField(
    'Security Groups',
    r?.network?.networkConfiguration?.networkModeConfig?.securityGroups,
  );

// AWS Bedrock AgentCore Gateway metadata
export const AgentCoreGatewayStatus = r =>
  MetaField('Status', r?.config?.status);
export const AgentCoreGatewayProtocolType = r =>
  MetaField('Protocol Type', r?.config?.protocolType);
export const AgentCoreGatewayAuthorizerType = r =>
  MetaField('Authorizer Type', r?.config?.authorizerType);
export const AgentCoreGatewayURL = r =>
  MetaField('Gateway URL', r?.config?.gatewayUrl, {
    colSpan: 2,
  });
export const AgentCoreGatewayPolicyEngineARN = r =>
  MetaField('Policy Engine ARN', r?.config?.policyEngineConfiguration?.arn, {
    colSpan: 2,
  });
export const AgentCoreGatewayPolicyEngineMode = r =>
  MetaField('Policy Engine Mode', r?.config?.policyEngineConfiguration?.mode);
export const AgentCoreGatewayCustomTransformLambdaARN = r =>
  MetaField(
    'Custom Transform Lambda ARN',
    r?.config?.customTransformConfiguration?.lambda?.arn,
    { colSpan: 2 },
  );
export const AgentCoreGatewayCreatedAt = r =>
  MetaField('Created At', r?.config?.createdAt, {
    time: 'dayjs',
  });
export const AgentCoreGatewayUpdatedAt = r =>
  MetaField('Updated At', r?.config?.updatedAt, {
    time: 'dayjs',
  });
export const AgentCoreGatewayTargetNames = r =>
  MetaField(
    'Target Name',
    r?.config?.targets?.map(target => target?.name),
  );
export const AgentCoreGatewayTargetStatuses = r =>
  MetaField(
    'Target Status',
    r?.config?.targets?.map(target => target?.status),
  );
export const AgentCoreGatewayTargetProtocolTypes = r =>
  MetaField(
    'Target Protocol Type',
    r?.config?.targets?.map(target => target?.protocolType),
  );
export const AgentCoreGatewayTargetRuntimeARNs = r =>
  MetaField(
    'Target Runtime ARN',
    r?.config?.targets?.map(
      target =>
        target?.targetConfiguration?.http?.agentcoreRuntime?.arn ??
        target?.targetConfiguration?.http?.agentCoreRuntime?.arn,
    ),
    { colSpan: 2 },
  );
export const AgentCoreGatewayTargetMCPLambdaARNs = r =>
  MetaField(
    'Target MCP Lambda ARN',
    r?.config?.targets?.map(
      target => target?.targetConfiguration?.mcp?.lambda?.lambdaArn,
    ),
    { colSpan: 2 },
  );
export const AgentCoreGatewayKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.kmsKeyArn, {
    colSpan: 2,
  });
export const AgentCoreGatewayWebACLARN = r =>
  MetaField('WAF Web ACL ARN', r?.security?.webAclArn, {
    colSpan: 2,
  });

// S3 table bucket metadata
export const TableBucketType = r =>
  MetaField('Table Bucket Type', r?.config?.type);
export const TableARN = r => MetaField('Table ARN', r?.config?.tableArn);
export const TableNamespaces = r =>
  MetaField(
    'Namespace',
    flatten(map(r?.config?.namespaces, o => o?.namespace)),
  );
export const TableNames = r => MetaField('Table Name', r?.config?.tableName);
export const BucketMaintenanceEnabledStatus = r =>
  MetaField(
    'Bucket Maintenance Enable Status',
    r?.config?.bucketMaintenanceConfiguration?.icebergUnreferencedFileRemoval
      ?.status ??
      r?.bucketMaintenance?.status ??
      r?.bucketMaintenanceConfiguration?.status ??
      r?.bucketMaintenanceStatus?.maintenanceJobStatus ??
      r?.status,
  );
export const IcebergCompactionEnabledStatus = r =>
  MetaField(
    'Iceberg Compaction Enable Status',
    r?.config?.tableMaintenanceConfiguration?.icebergCompaction?.status ??
      r?.icebergCompaction?.status ??
      r?.configuration?.icebergCompaction?.status ??
      r?.icebergCompactionStatus,
  );
export const IcebergSnapshotManagementEnabledStatus = r =>
  MetaField(
    'Iceberg Snapshot Management Enable Status',
    r?.config?.tableMaintenanceConfiguration?.icebergSnapshotManagement
      ?.status ??
      r?.icebergSnapshotManagement?.status ??
      r?.configuration?.icebergSnapshotManagement?.status ??
      r?.icebergSnapshotManagementStatus,
  );
export const IcebergUnreferencedFileRemovalEnabledStatus = r =>
  MetaField(
    'Table Iceberg Unreferenced File Removal Enable Status',
    r?.config?.tableMaintenanceConfiguration?.icebergUnreferencedFileRemoval
      ?.status,
  );
export const IcebergCompactionJobStatus = r =>
  MetaField(
    'Iceberg Compaction Job Status',
    r?.config?.maintenanceJobStatus?.icebergCompaction?.status ??
      r?.maintenanceJobStatus?.icebergCompaction?.status ??
      r?.icebergCompactionJob?.status ??
      r?.icebergCompactionJobStatus ??
      r?.status,
  );
export const IcebergSnapshotManagementJobStatus = r =>
  MetaField(
    'Iceberg Snapshot Management Job Status',
    r?.config?.maintenanceJobStatus?.icebergSnapshotManagement?.status ??
      r?.maintenanceJobStatus?.icebergSnapshotManagement?.status ??
      r?.icebergSnapshotManagementJob?.status ??
      r?.icebergSnapshotManagementJobStatus ??
      r?.status,
  );
export const IcebergUnreferencedFileRemovalJobStatus = r =>
  MetaField(
    'Iceberg Unreferenced File Removal Job Status',
    r?.config?.maintenanceJobStatus?.icebergUnreferencedFileRemoval?.status,
  );
export const TableMetadataLocation = r =>
  MetaField(
    'Metadata Location',
    r?.config?.metadataLocation ?? r?.metadataLocation,
  );
export const TableReplicationRole = r =>
  MetaField('Replication Role', r?.config?.replicationConfiguration?.role);
export const TableReplicationRuleStatus = r =>
  MetaField(
    'Replication Rule Status',
    r?.config?.replicationConfiguration?.rules?.map(rule => rule?.status) ??
      r?.replication?.status ??
      r?.replicationStatus ??
      r?.status,
  );
export const ReplicationDestinationTableBucketARN = r =>
  MetaField(
    'Replication Destination Table Bucket ARN',
    r?.config?.replicationConfiguration?.rules?.map(
      rule => rule?.destination?.tableBucketARN,
    ) ??
      r?.replication?.tableBucketARN ??
      r?.destination?.tableBucketARN ??
      r?.tableBucketARN,
  );
export const TableBucketEncryptionType = r =>
  MetaField(
    'Encryption Type',
    r?.security?.encryptionConfiguration?.sseAlgorithm,
  );
export const TableBucketKMSKeyARN = r => MetaField('KMS Key ARN', r?.kmsKeyArn);
export const TableBucketARN = r =>
  MetaField('Table Bucket ARN', r?.identifier?.arn, { colSpan: 2 });
export const TableBucketOwnerAccountID = r =>
  MetaField('Owner Account ID', r?.identifier?.ownerAccountId);
export const TableBucketSecurityKMSKeyARN = r =>
  MetaField('KMS Key ARN', r?.security?.encryptionConfiguration?.kmsKeyArn, {
    colSpan: 2,
  });

// AWS Glue table metadata
const getGlueField = (r, field) =>
  r?.[field] ?? r?.config?.[field] ?? r?.identifier?.[field];
const getGlueStorageDescriptor = r =>
  r?.StorageDescriptor ?? r?.config?.StorageDescriptor ?? r?.storageDescriptor;
const getGlueOptimizer = (r, optimizer) =>
  r?.config?.tableOptimizers?.[optimizer] ??
  r?.[optimizer] ??
  r?.tableOptimizers?.[optimizer] ??
  r?.optimizers?.[optimizer];

export const GlueTableCreatedAt = r =>
  MetaField('Created At', getGlueField(r, 'CreateTime'), { time: 'dayjs' });
export const GlueTableUpdatedAt = r =>
  MetaField('Updated At', getGlueField(r, 'UpdateTime'), { time: 'dayjs' });
export const GlueTableLastAccessedAt = r =>
  MetaField('Last Accessed At', getGlueField(r, 'LastAccessTime'), {
    time: 'dayjs',
  });
export const GlueTableType = r =>
  MetaField('Table Type', getGlueField(r, 'TableType'));
export const GlueTableName = r => MetaField('Table Name', 'None');
export const GlueTableParameters = r =>
  MetaField('Table Parameters', getGlueField(r, 'Parameters'));
export const GlueTableRegisteredWithLakeFormation = r =>
  MetaField(
    'Registered With Lake Formation',
    getGlueField(r, 'IsRegisteredWithLakeFormation'),
  );
export const GlueTableMultiDialectView = r =>
  MetaField('Multi-Dialect View', getGlueField(r, 'IsMultiDialectView'));
export const GlueTableMaterializedView = r =>
  MetaField('Materialized View', getGlueField(r, 'IsMaterializedView'));
export const GlueTableStorageLocation = r =>
  MetaField(
    'Storage Location',
    getGlueStorageDescriptor(r)?.Location ?? getGlueField(r, 'Location'),
  );
export const GlueTableInputFormat = r =>
  MetaField('Input Format', getGlueStorageDescriptor(r)?.InputFormat);
export const GlueTableOutputFormat = r =>
  MetaField('Output Format', getGlueStorageDescriptor(r)?.OutputFormat);
export const GlueTableCompressed = r =>
  MetaField('Compressed', getGlueStorageDescriptor(r)?.Compressed);
export const GlueIcebergTableLocation = r =>
  MetaField(
    'Iceberg Table Location',
    r?.config?.IcebergTableMetadata?.Location ??
      r?.icebergTable?.Location ??
      r?.iceberg?.Location ??
      r?.icebergLocation ??
      getGlueField(r, 'Location'),
  );
export const GlueIcebergTableUUID = r =>
  MetaField(
    'Iceberg Table UUID',
    r?.config?.IcebergTableMetadata?.TableUuid ??
      r?.icebergTable?.TableUuid ??
      r?.iceberg?.TableUuid ??
      getGlueField(r, 'TableUuid'),
  );
export const GlueIcebergFormatVersion = r =>
  MetaField(
    'Iceberg Format Version',
    r?.config?.IcebergTableMetadata?.FormatVersion ??
      r?.icebergTable?.FormatVersion ??
      r?.iceberg?.FormatVersion ??
      getGlueField(r, 'FormatVersion'),
  );
export const GlueCompactionEnabled = r =>
  MetaField(
    'Compaction Enabled',
    getGlueOptimizer(r, 'compaction')?.configuration?.enabled ??
      getGlueOptimizer(r, 'compaction')?.enabled ??
      getGlueField(r, 'enabled'),
  );
export const GlueCompactionStrategy = r =>
  MetaField(
    'Compaction Strategy',
    getGlueOptimizer(r, 'compaction')?.configuration?.compactionConfiguration
      ?.icebergConfiguration?.strategy ??
      getGlueOptimizer(r, 'compaction')?.configuration?.compactionConfiguration
        ?.strategy ??
      getGlueOptimizer(r, 'compaction')?.strategy ??
      getGlueField(r, 'strategy'),
  );
export const GlueCompactionLastRunStatus = r =>
  MetaField(
    'Compaction Last Run Status',
    getGlueOptimizer(r, 'compaction')?.lastRun?.eventType ??
      getGlueOptimizer(r, 'compaction')?.eventType ??
      getGlueField(r, 'eventType'),
  );
export const GlueCompactionFilesCompacted = r =>
  MetaField(
    'Compaction Files Compacted (Last Run)',
    getGlueOptimizer(r, 'compaction')?.lastRun?.compactionMetrics
      ?.icebergMetrics?.numberOfFilesCompacted ??
      getGlueOptimizer(r, 'compaction')?.lastRun?.compactionMetrics
        ?.icebergMetrics?.NumberOfFilesCompacted ??
      getGlueOptimizer(r, 'compaction')?.lastRun?.NumberOfFilesCompacted ??
      getGlueOptimizer(r, 'compaction')?.NumberOfFilesCompacted ??
      getGlueField(r, 'NumberOfFilesCompacted'),
  );
export const GlueRetentionEnabled = r =>
  MetaField(
    'Retention Enabled',
    getGlueOptimizer(r, 'retention')?.configuration?.enabled ??
      getGlueOptimizer(r, 'retention')?.enabled ??
      getGlueField(r, 'enabled'),
  );
export const GlueRetentionPeriod = r =>
  MetaField(
    'Retention Period (Days)',
    getGlueOptimizer(r, 'retention')?.configuration?.retentionConfiguration
      ?.icebergConfiguration?.snapshotRetentionPeriodInDays ??
      getGlueOptimizer(r, 'retention')?.snapshotRetentionPeriodInDays ??
      getGlueField(r, 'snapshotRetentionPeriodInDays'),
  );
export const GlueRetentionLastRunStatus = r =>
  MetaField(
    'Retention Last Run Status',
    getGlueOptimizer(r, 'retention')?.lastRun?.eventType ??
      getGlueOptimizer(r, 'retention')?.eventType ??
      getGlueField(r, 'eventType'),
  );
export const GlueRetentionDataFilesDeleted = r =>
  MetaField(
    'Retention Data Files Deleted (Last Run)',
    getGlueOptimizer(r, 'retention')?.lastRun?.retentionMetrics?.icebergMetrics
      ?.numberOfDataFilesDeleted ??
      getGlueOptimizer(r, 'retention')?.lastRun?.retentionMetrics
        ?.icebergMetrics?.NumberOfDataFilesDeleted ??
      getGlueOptimizer(r, 'retention')?.lastRun?.NumberOfDataFilesDeleted ??
      getGlueOptimizer(r, 'retention')?.NumberOfDataFilesDeleted ??
      getGlueField(r, 'NumberOfDataFilesDeleted'),
  );
export const GlueOrphanFileDeletionEnabled = r =>
  MetaField(
    'Orphan File Deletion Enabled',
    getGlueOptimizer(r, 'orphan_file_deletion')?.configuration?.enabled ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.configuration?.enabled ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.enabled ??
      getGlueField(r, 'enabled'),
  );
export const GlueOrphanFileRetentionPeriod = r =>
  MetaField(
    'Orphan File Retention Period (Days)',
    getGlueOptimizer(r, 'orphan_file_deletion')?.configuration
      ?.orphanFileDeletionConfiguration?.icebergConfiguration
      ?.orphanFileRetentionPeriodInDays ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.configuration
        ?.orphanFileDeletionConfiguration?.icebergConfiguration
        ?.orphanFileRetentionPeriodInDays ??
      getGlueOptimizer(r, 'orphanFileDeletion')
        ?.orphanFileRetentionPeriodInDays ??
      getGlueField(r, 'orphanFileRetentionPeriodInDays'),
  );
export const GlueOrphanFileDeletionLastRunStatus = r =>
  MetaField(
    'Orphan File Deletion Last Run Status',
    getGlueOptimizer(r, 'orphan_file_deletion')?.lastRun?.eventType ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.lastRun?.eventType ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.eventType ??
      getGlueField(r, 'eventType'),
  );
export const GlueOrphanFilesDeleted = r =>
  MetaField(
    'Orphan Files Deleted (Last Run)',
    getGlueOptimizer(r, 'orphan_file_deletion')?.lastRun
      ?.orphanFileDeletionMetrics?.icebergMetrics?.numberOfOrphanFilesDeleted ??
      getGlueOptimizer(r, 'orphan_file_deletion')?.lastRun
        ?.orphanFileDeletionMetrics?.icebergMetrics
        ?.NumberOfOrphanFilesDeleted ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.lastRun
        ?.orphanFileDeletionMetrics?.icebergMetrics
        ?.numberOfOrphanFilesDeleted ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.lastRun
        ?.orphanFileDeletionMetrics?.icebergMetrics
        ?.NumberOfOrphanFilesDeleted ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.lastRun
        ?.NumberOfOrphanFilesDeleted ??
      getGlueOptimizer(r, 'orphanFileDeletion')?.NumberOfOrphanFilesDeleted ??
      getGlueField(r, 'NumberOfOrphanFilesDeleted'),
  );
export const GluePartitionValues = r =>
  MetaField(
    'Partition Values',
    r?.config?.partitions?.map(partition => partition?.Values) ??
      r?.partitionValues ??
      getGlueField(r, 'Values') ??
      r?.partitions?.map(partition => partition?.Values),
  );
export const GluePartitionStorageLocation = r =>
  MetaField(
    'Partition Storage Location',
    r?.config?.partitions?.map(
      partition => partition?.StorageDescriptor?.Location,
    ) ??
      r?.partitionLocations ??
      r?.partitions?.map(partition => partition?.StorageDescriptor?.Location) ??
      getGlueField(r, 'Location'),
  );
export const GluePartitionLastAnalyzedTime = r =>
  MetaField(
    'Partition Last Analyzed Time',
    r?.config?.partitions?.map(partition => partition?.LastAnalyzedTime) ??
      r?.partitionLastAnalyzedTimes ??
      getGlueField(r, 'LastAnalyzedTime') ??
      r?.partitions?.map(partition => partition?.LastAnalyzedTime),
  );
export const GlueColumnNames = r =>
  MetaField(
    'Column Name',
    r?.config?.columnStatistics?.map(column => column?.ColumnName) ??
      r?.ColumnName ??
      getGlueStorageDescriptor(r)?.Columns?.map(column => column?.Name),
  );
export const GlueColumnTypes = r =>
  MetaField(
    'Column Type',
    r?.config?.columnStatistics?.map(column => column?.ColumnType) ??
      r?.ColumnType ??
      getGlueStorageDescriptor(r)?.Columns?.map(column => column?.Type),
  );
export const GlueTableVersionIDs = r =>
  MetaField(
    'Table Version ID',
    r?.config?.tableVersions?.map(version => version?.VersionId) ??
      r?.VersionId ??
      r?.tableVersions?.map(version => version?.VersionId),
  );
export const GlueTableVersionUpdatedAt = r =>
  MetaField(
    'Table Version Updated At',
    r?.config?.tableVersions?.map(version => version?.Table?.UpdateTime) ??
      r?.tableVersionUpdateTimes ??
      getGlueField(r, 'UpdateTime') ??
      r?.tableVersions?.map(version => version?.UpdateTime),
  );
export const GlueDatabaseName = r =>
  MetaField('Database Name', getGlueField(r, 'DatabaseName'));
export const GlueCatalogID = r =>
  MetaField('Catalog ID', getGlueField(r, 'CatalogId'));
export const GlueCatalogARN = r =>
  MetaField('Catalog ARN', getGlueField(r, 'ResourceArn'));

// IAMUser metadata
export const Groups = r =>
  MetaField(
    'Groups',
    map(r?.config?.Groups, o => o?.GroupId),
    { linkResourceType: NativeResources.IamGroup },
  );
export const MFAEnabled = r =>
  MetaField(
    'MFA',
    some(r?.contexts, o => o.key === 'mfa') ? 'Enabled' : 'Disabled',
  );
export const UserState = r => MetaField('State', 'Active');
export const PasswordLastUsed = r =>
  MetaField(
    'Password last used',
    r?.password_enabled?.password_last_used,
    { time: 'dayjs' },
    //dayjs(r?.security?.PasswordLastUsed).format('llll'),
  );
export const PasswordReset = r =>
  MetaField(
    'Password reset required',
    r?.security?.LoginProfile?.PasswordResetRequired,
  );
export const AccessKeys = r => MetaField('Access keys', r?.active_access_keys);
export const AccessKeysList = r =>
  MetaField(
    'Access key names',
    r?.security?.AccessKeys?.map(k => k?.AccessKeyId),
  );
export const UserAttachedPolicies = r =>
  MetaField('Attached policies', r?.config?.UserAttachedPolicies?.length);
export const UserAttachedPoliciesList = r =>
  MetaField(
    'Attached policies',
    r?.config?.UserAttachedPolicies?.map(p => p?.PolicyName),
  );
export const PermissionBoundary = r =>
  MetaField('Permission boundary', r?.config?.PermissionsBoundary?.PolicyName);
export const UserInlinePolicies = r =>
  MetaField(
    'Inline policies',
    map(r?.config?.UserInlinePolicies, o => o?.PolicyName),
    { withListAsTooltip: true },
  );

//Snowflake
export const Username = r => MetaField('Username', r?.config?.name);
export const LoginName = r => MetaField('Login name', r?.config?.login_name);
export const SnowflakeID = r => MetaField('ID', r?.resource_id);
export const Comment = r => MetaField('Comment', r?.config?.comment);
export const IdentityProvider = r =>
  MetaField('Identity Provider', r?.provider);
export const Emails = r => MetaField('Emails', '');
export const Active = r => MetaField('Active', r?.config?.disabled === 'false');
export const Roles = r =>
  MetaField('Roles', r?.connection?.SnowflakeRoles || r?.config?.roles);
export const Privileges = r => MetaField('Privileges', '');
export const AssignedUser = r =>
  MetaField('Assigned user', r?.config?.assigned_to_users);
export const IsDefault = r =>
  MetaField('Is default', r?.config?.is_default === 'Y');
export const IsCurrent = r =>
  MetaField('Is current', r?.config?.is_current === 'Y');
export const IsInherited = r =>
  MetaField('Is inherited', r?.config?.is_inherited === 'Y');
export const AssignedToOtherRoles = r =>
  MetaField('Assigned to other roles', r?.config?.granted_to_roles);
export const GrantedRoles = r =>
  MetaField('Granted roles', r?.config?.granted_roles);
export const Schemas = r =>
  MetaField(
    'Schemas',
    map(r?.config?.schemas, o => o.name),
    { withListAsTooltip: true },
  );
export const Schema = r => MetaField('Schema', r?.config?.schema_name);
export const Tables = r => MetaField('Tables', r?.tables?.length);
export const Views = r => MetaField('Views', '');
export const IsTransient = r =>
  MetaField('Is transient', r?.config?.is_transient);
export const SnowflakeDatabase = r =>
  MetaField('Database', r?.config?.db_name || r?.config?.database_name);
export const ClusteringKey = r =>
  MetaField('Clustering key', r?.config?.cluster_by);
export const RowCount = r => MetaField('Row count', r?.config?.rows);
export const Bytes = r => MetaField('Bytes', r?.config?.bytes);
export const RetentionTime = r =>
  MetaField('Retention time', r?.config?.retention_time);
export const ReplicationState = r =>
  MetaField(
    'Replication state',
    r?.config?.replication_state ?? r?.config?.replication_status,
  );
export const AutoClusteringOn = r =>
  MetaField('Auto Clustering On', r?.config?.automatic_clustering);
export const RowAccessPolicy = r =>
  MetaField('Row access policy', r?.config?.row_access_policy);

// AccountRoot metadata
export const VirtualMFAEnabled = r =>
  MetaField('Virtual MFA enabled', r?.virtual_mfa?.virtual_mfa_enabled);
export const HardwareMFAEnabled = r =>
  MetaField('Hardware MFA enabled', r?.hardware_mfa_enabled);
export const AccessKeysUsed = r =>
  MetaField(
    'Access keys used',
    'Key 1: ' +
      (r?.access_keys?.access_key_1_active ? 'Yes' : 'No') +
      ', Key 2: ' +
      (r?.access_keys?.access_key_2_active ? 'Yes' : 'No'),
  );
export const LastUsedRegion = r =>
  MetaField('Access keys last used region', '~');
export const LastUsedAt = r => MetaField('Access keys last used at', '~');
export const ServiceControlPolicy = r =>
  MetaField(
    'Service control policy',
    filter(
      r?.config?.PoliciesAtAccount,
      o => o?.Type === 'SERVICE_CONTROL_POLICY',
    )?.map(s => ({ name: s?.Name, id: s?.Id })),
    { linkResourceType: NativeResources.OrganizationPolicy },
  );

// IAMRole metadata
export const CrossAccount = r =>
  MetaField(
    'Cross account',
    r?.config?.CrossAccountFlagAriksa ?? r?.cross_account_access,
  );
export const Path = r => MetaField('Path', r?.config?.Path);
export const MaxSessionDuration = r =>
  MetaField('Max session duration', r?.config?.MaxSessionDuration);
// export const RoleAttachedPolicies = (r) => MetaField('Attached Policies', r?.config?.RoleAttachedPolicies?.length);
export const RoleAttachedPoliciesList = r =>
  MetaField(
    'Attached policies',
    r?.config?.RoleAttachedPolicies?.map(p => p?.PolicyName),
  );
export const RoleInlinePoliciesList = r =>
  MetaField(
    'Inline policies',
    r?.config?.RoleInlinePolicies?.map(p => p?.PolicyName),
  );
export const PolicyType = r => MetaField('Type', r?.managed_by);
export const LatestVersion = r =>
  MetaField('Latest version', r?.latest_version);
export const RoleLastUsedAt = r =>
  MetaField('Last used at', r?.config?.RoleLastUsed?.LastUsedDate);
export const RoleLastUsedRegion = r =>
  MetaField('Last used region', r?.config?.RoleLastUsed?.Region);
export const RoleState = r =>
  MetaField(
    'State',
    !!r?.config?.RoleLastUsed?.LastUsedDate ? 'Used' : 'Unused',
  );

//IAM Policy metadata
export const AllowedActions = r =>
  MetaField('Allowed actions', r?.allowed_action_count);
export const UpdatedAt = r =>
  MetaField(
    'Updated at',
    r?.config?.UpdateDate ||
      r?.config?.updatedAt ||
      r?.config?.lastUpdatedAt ||
      r?.config?.lastModifiedTime,
    {
      time: 'dayjs',
    },
  );
export const DeniedActions = r =>
  MetaField('Denied actions', r?.denied_action_count);
export const AllowedServices = r =>
  MetaField('Allowed services', r?.allowed_service_count);
export const DeniedServices = r =>
  MetaField('Denied services', r?.denied_service_count);
export const AttachedToRoles = r =>
  MetaField(
    'Attached roles',
    r?.config?.PolicyRoles?.map(o => ({ id: o?.RoleId, name: o?.RoleName })),
    { linkResourceType: NativeResources.IamRole, colSpan: 2 },
  );
export const Conditional = r => MetaField('Conditional', r?.conditional);
export const DefaultVersion = r =>
  MetaField('Default Version', r?.identifier?.DefaultVersionId);
export const AllServiceActionsPermitted = r =>
  MetaField('All service sections permitted?', '~');
export const AllResources = r => MetaField('All resources', '~');
export const WildcardForResources = r =>
  MetaField('Wildcard for resources', r?.wildcard_resource);
export const WildcardForActions = r =>
  MetaField('Wildcard for actions', r?.wildcard_action);
export const PolicyDescription = r => MetaField('Description', '~');
export const IAMPolicyState = r =>
  MetaField('State', r?.attachment_count > 0 ? 'Attached' : 'Unattached');

// Policy
export const CloudManaged = r => MetaField('Cloud managed', '~');
export const AttachedUsers = r =>
  MetaField(
    'Attached users',
    r?.config?.PolicyUsers?.map(o => ({ name: o?.UserName, id: o?.UserId })),
    { linkResourceType: NativeResources.IamUser },
  );
export const AttachedGroups = r =>
  MetaField(
    'Attached groups',
    r?.config?.PolicyGroups?.map(o => ({ name: o?.GroupName, id: o?.GroupId })),
    { linkResourceType: NativeResources.IamGroup },
  );
export const WildcardResources = r => MetaField('Wildcard resources', '~');
export const WildcardActions = r => MetaField('Wildcard actions', '~');
export const Conditions = r => MetaField('Conditions', '~');
export const Permissions = r =>
  MetaField('Permissions', '~', { withListAsTooltip: true });
export const Principals = r =>
  MetaField('Principal(s)', '~', { withListAsTooltip: true });

// ElasticSearch
export const AvailabilityZones = r =>
  MetaField(
    'Availability zones',
    r?.network?.VPCOptions?.AvailabilityZones || r?.config?.AvailabilityZones,
  );
export const SecurityGroupsList = r =>
  MetaField('Security groups', r?.network?.VPCOptions?.SecurityGroupIds);
export const Endpoints = r =>
  MetaField('Endpoint', r?.config?.Endpoint, { colSpan: 2 });
export const Version = r =>
  MetaField('Version', r?.config?.ElasticsearchVersion || r?.config?.version);
export const ClusterInstanceType = r =>
  MetaField(
    'Cluster instance type',
    r?.config?.ElasticsearchClusterConfig?.InstanceType,
  );
export const DedicatedMaster = r =>
  MetaField(
    'Dedicated master?',
    r?.config?.ElasticsearchClusterConfig?.DedicatedMasterEnabled,
  );
export const MasterInstanceType = r =>
  MetaField(
    'Master instance type',
    r?.config?.ElasticsearchClusterConfig?.DedicatedMasterType,
  );
export const WarmStorageEnabled = r =>
  MetaField(
    'Warm storage enabled?',
    r?.config?.ElasticsearchClusterConfig?.WarmEnabled,
  );
export const WarmInstanceType = r =>
  MetaField(
    'Warm instance type',
    r?.config?.ElasticsearchClusterConfig?.WarmType,
  );
export const WarmNodesInCluster = r => MetaField('Warm nodes in cluster', '~');
export const ColdStorage = r =>
  MetaField(
    'Cold storage?',
    r?.config?.ElasticsearchClusterConfig?.ColdStorageOptions?.Enabled,
  );
export const AttachedVolumeType = r =>
  MetaField('Attached volume type', r?.storage?.EBSOptions?.VolumeType);
export const AttachedVolumeSize = r =>
  MetaField('Attached volume size', r?.storage?.EBSOptions?.VolumeSize);
export const VolumeIOPS = r =>
  MetaField('Volume IOPS', r?.storage?.EBSOptions?.Iops);
export const EncryptionAtRest = r =>
  MetaField(
    'Encryption at rest?',
    r?.security?.EncryptionAtRestOptions?.Enabled,
  );
export const EncryptionKeyID = r =>
  MetaField(
    'Encryption key ID',
    r?.security?.EncryptionAtRestOptions?.KmsKeyId,
  );
export const LogsOptions = r =>
  MetaField(
    'Logs option',
    map(r?.config?.LogPublishingOptions, (value, key) => key),
  );
export const CloudWatchLogARN = r =>
  MetaField(
    'Cloud watch Logs ARN',
    map(r?.config?.LogPublishingOptions, o => o.CloudWatchLogsLogGroupArn),
  );
export const UpdateAvailable = r =>
  MetaField(
    'Update available?',
    r?.config?.ServiceSoftwareOptions?.UpdateAvailable,
  );
export const UpdateStatus = r =>
  MetaField('Update status', r?.config?.ServiceSoftwareOptions?.UpdateStatus);
export const EnforceHTTPSOnEndpoint = r =>
  MetaField(
    'Enforce HTTPS on endpoint',
    r?.config?.DomainEndpointOptions?.EnforceHTTPS,
  );
export const TLSPolicyOnEndpoint = r =>
  MetaField(
    'TLS policy on endpoint',
    r?.config?.DomainEndpointOptions?.TLSSecurityPolicy,
  );
export const CustomEndpoint = r =>
  MetaField(
    'Custom endpoint?',
    r?.config?.DomainEndpointOptions?.CustomEndpointEnabled,
  );
export const CustomEndpointCertificateARN = r =>
  MetaField(
    'Custom endpoint certificate ARN',
    r?.config?.DomainEndpointOptions?.CustomEndpointCertificateArn,
  );
export const SAMLEntityID = r =>
  MetaField(
    'SAML - Entity ID',
    r?.security?.AdvancedSecurityOptions?.SAMLOptions?.Idp?.EntityId,
  );
export const SAMLSubjectKey = r =>
  MetaField(
    'SAML - Subject key',
    r?.security?.AdvancedSecurityOptions?.SAMLOptions?.SubjectKey,
  );

//SNS
export const SNSFIFO = r => MetaField('FIFO', r?.config?.FifoTopic === 'true');
export const SubscriptionsConfirmed = r =>
  MetaField('Subscriptions confirmed', r?.config?.SubscriptionsConfirmed);
export const SubscriptionsPending = r =>
  MetaField('Subscriptions pending', r?.config?.SubscriptionsPending);
export const SubscriptionsDeleted = r =>
  MetaField('Subscriptions deleted', r?.config?.SubscriptionsDeleted);
export const MessagesEncrypted = r =>
  MetaField('Messages encrypted', !!r?.identifier?.KmsMasterKeyId);
export const KeyIDForEncryption = r =>
  MetaField('Key ID for encryption', r?.identifier?.KmsMasterKeyId);
export const NumberOfAttachedPolicies = r =>
  MetaField(
    'Attached policies',
    sum([
      !!r?.config?.Policy ? 1 : 0,
      !!r?.config?.EffectiveDeliveryPolicy ? 1 : 0,
    ]),
  );
export const AttachedPolicies = r =>
  MetaField('Attached policies', [
    !!r?.config?.Policy ? 'Access Policy' : null,
    !!r?.config?.EffectiveDeliveryPolicy ? 'Delivery Policy' : null,
  ]);
export const Endpoint = r =>
  MetaField(
    'Endpoint',
    r?.config?.resources?.map(o => o?.resourceMethods?.methodIntegration?.uri),
  );
export const Protocol = r => MetaField('Protocol', '~');
export const TopicARN = r =>
  MetaField('Topic ARN', r?.config?.NotificationConfiguration?.TopicArn);
export const RedrivePolicy = r => MetaField('Redrive policy', '~');
export const SNSEncryptionAtRest = r =>
  MetaField(
    'Encryption at rest',
    find(r?.contexts, o => o.key === 'encryption')?.value,
  );
export const EncryptionInTransit = r =>
  MetaField('Encryption in transit', 'Enabled');
export const ContentBasedDedupe = r =>
  MetaField(
    'Content based dedupe',
    r?.config?.ContentBasedDeduplication === 'true',
  );

//SQS
export const SQSFIFO = r => MetaField('FIFO', r?.config?.FifoQueue);
export const WhoCanSend = r => MetaField('Who can send', r?.config?.WhoCanSend);
export const WhoCanReceive = r =>
  MetaField('Who can receive', r?.config?.WhoCanReceive);
export const SQSType = r =>
  MetaField('Type', r?.config?.FifoQueue ? 'FIFO' : 'Standard');
export const ReceiveMessageWaitTime = r =>
  MetaField(
    'Receive message wait time (seconds)',
    r?.config?.ReceiveMessageWaitTimeSeconds,
  );
export const DeliveryDelay = r =>
  MetaField('Delivery delay (seconds)', r?.config?.DelaySeconds);
export const VisibilityTimeout = r =>
  MetaField('Visibility timeout (seconds)', r?.config?.VisibilityTimeout);
export const DeadLetterQueue = r =>
  MetaField('Dead letter queue', r?.config?.DeadLetterQueue);
export const FIFOThroughputLimit = r =>
  MetaField('FIFO throughput limit', r?.config?.FifoThroughputLimit);
export const HasServerSideEncryption = r =>
  MetaField(
    'Server-side encryption',
    !!r?.identifier?.KmsMasterKeyId || r?.security?.SqsManagedSseEnabled,
  );
export const SQSServerSideEncryption = r =>
  MetaField('Server-side encryption', '~');
export const KeyReusePeriod = r =>
  MetaField(
    'Key reuse period (seconds)',
    r?.config?.KmsDataKeyReusePeriodSeconds,
  );
export const NumberOfMessages = r =>
  MetaField('Number of messages', r?.config?.ApproximateNumberOfMessages);
export const DelayedMessages = r =>
  MetaField('Delayed messages', r?.config?.ApproximateNumberOfMessagesDelayed);
export const MessagesNotVisible = r =>
  MetaField(
    'Messages not visible',
    r?.config?.ApproximateNumberOfMessagesNotVisible,
  );
export const MaxMessageSize = r =>
  MetaField('Max message size (in KB)', r?.config?.MaximumMessageSize);
export const MessageRetentionPeriod = r =>
  MetaField(
    'Message retention period (seconds)',
    r?.config?.MessageRetentionPeriod,
  );

//Organization
export const AccountARN = r => MetaField('Account ARN', r?.arn, { colSpan: 2 });
export const Email = r =>
  MetaField(
    'Email',
    r?.identifier?.MasterAccountEmail ||
      r?.config?.email ||
      r?.config?.Email ||
      r?.config?.OrganizationAccountDetails?.Email,
  );
export const FeatureSet = r => MetaField('Feature set', r?.config?.FeatureSet);
export const NumberOfUsers = r =>
  MetaField('Users', r?.total_user_count || r?.users?.length);
export const NumberOfRoles = r =>
  MetaField('Roles', r?.total_role_count || r?.roles?.length);
export const NumberOfGroups = r =>
  MetaField(
    'Groups',
    r?.total_group_count ??
      r?.config?.DefaultCacheBehavior?.TrustedKeyGroups?.Quantity ??
      r?.groups?.length,
  );
export const ViewerProtocolPolicy = r =>
  MetaField(
    'Viewer Protocol Policy',
    r?.config?.DefaultCacheBehavior?.ViewerProtocolPolicy,
  );
export const NumberOfPolicies = r =>
  MetaField('Policies', r?.total_policy_count || r?.connection?.Policy?.length);
export const OrgUnits = r =>
  MetaField(
    'Organization units',
    r?.config?.OrganizationalUnitsAtRoot?.map(o => ({
      name: o?.Name ?? o?.Id,
      id: o?.Id,
    })) || r?.config?.ChildOrganizationalUnits?.map(o => o.Id),
    { linkResourceType: NativeResources.OrganizationUnit },
  );
export const IdentityPolicies = r =>
  MetaField('Identity policies', r?.identity_policies?.length);
export const ResourcePolicies = r =>
  MetaField('Resource policies', r?.resource_policies?.length);
export const TagPolicies = r =>
  MetaField('Tag policies', r?.tag_policies?.length);
export const SCP = r => MetaField('SCP', r?.scp_policies?.length);
export const OrgAccounts = r =>
  MetaField(
    'Accounts',
    r?.config?.AccountsAtRoot?.map(o => ({
      name: o?.Name ?? o?.Id,
      id: o?.Id,
    })),
    { linkResourceType: NativeResources.AwsAccount },
  );

// Organization Unit
export const Organization = r =>
  MetaField('Organization', r?.identifier?.organizationId);
export const ParentOrganization = r =>
  MetaField('Parent organization', r?.parent_organization, {
    colSpan: 2,
    linkResourceType: NativeResources.AwsOrganization,
  });
export const OrgUnitPolicies = r =>
  MetaField(
    'Organization',
    map(r?.config?.PoliciesAtOU, o => o?.Name),
  );
export const Accounts = r =>
  MetaField(
    'Accounts',
    r?.accounts || map(r?.config?.ChildAccounts, o => o?.Id),
  );
export const OrgUnitAccounts = r =>
  MetaField(
    'Accounts',
    r?.config?.ChildAccounts?.map(o => o?.Id),
    {
      linkResourceType: NativeResources.AwsAccount,
    },
  );

// AWS Account
export const IsOnboarded = r =>
  MetaField('Is onboarded', r?.config?.IsOnboarded);
export const ManagementAccountId = r =>
  MetaField('Management account', r?.identifier?.ManagementAccountId, {
    linkResourceType: NativeResources.AwsAccount,
  });

// RDS Instance
export const InternetAccessible = r =>
  MetaField('Internet accessible', r?.config?.PubliclyAccessible);
export const DNSAddress = r =>
  MetaField(
    'DNS address',
    r?.identifier?.Endpoint?.Address + ' : ' + r?.identifier?.Endpoint?.Port,
    { colSpan: 2 },
  );
export const RDSUrl = r => MetaField('URL', r?.identifier?.Address?.Endpoint);
export const InstanceClass = r =>
  MetaField('Instance class', r?.config?.DBInstanceClass);
export const ClusterId = r =>
  MetaField('Cluster Id', r?.identifier?.DBClusterIdentifier);
export const AssociatedStorage = r =>
  MetaField('Allocated storage', r?.storage?.AllocatedStorage);
export const StorageType = r =>
  MetaField('Storage Type', r?.storage?.StorageType || r?.storageType);
export const RDSKeyId = r => MetaField('Key ID', r?.identifier?.KmsKeyId);
export const StorageEncrypted = r =>
  MetaField('Storage encrypted', r?.storage?.StorageEncrypted);
export const CaCertificate = r =>
  MetaField('CA Certificate', r?.identifier?.CACertificateIdentifier);
export const BackupWindow = r =>
  MetaField('Backup window', r?.config?.PreferredBackupWindow);
export const BackupRetentionPeriod = r =>
  MetaField('Backup retention period', r?.config?.BackupRetentionPeriod);
export const DatabaseEngine = r =>
  MetaField('Database engine', r?.config?.Engine);
export const DatabaseEngineVersion = r =>
  MetaField('Database engine version', r?.config?.EngineVersion);
export const MasterUsername = r =>
  MetaField('Master username', r?.security?.MasterUsername);
export const LicenseModel = r =>
  MetaField('License model', r?.config?.LicenseModel);
export const IAMAuthEnabled = r =>
  MetaField('IAM auth enabled', r?.security?.IAMDatabaseAuthenticationEnabled);
export const DeleteProtection = r =>
  MetaField('Delete protection', r?.config?.DeletionProtection);
export const MultiAZ = r => MetaField('Multi AZ', r?.config?.MultiAZ);
export const RDSSecurityGroupList = r =>
  MetaField(
    'Security groups',
    r?.network?.VpcSecurityGroups?.map(e => e?.VpcSecurityGroupId),
    { linkResourceType: NativeResources.SecurityGroup },
  );

// RDSCluster
export const RDSClusterMembers = r =>
  MetaField(
    'Members',
    r?.secuirty?.DBClusterMembers?.map(e => e.DBInstanceIdentifier),
  );
export const RDSClusterUrl = r => MetaField('URL', r?.identifier?.Endpoint);
export const PreferredBackupWindow = r =>
  MetaField('Preferred backup window', r?.config?.PreferredBackupWindow);
export const ReaderUrl = r =>
  MetaField('Reader URL', r?.config?.ReaderEndpoint);
export const HttpEndpointEnabled = r =>
  MetaField('HTTP endpoint enabled', r?.config?.HttpEndpointEnabled);
export const LastRecoverableTimestamp = r =>
  MetaField('Last Recoverable Timestamp', r?.config?.LatestRestorableTime);

// DynamoDB
export const LastIncreaseDateTime = r =>
  MetaField(
    'Last increase date',
    r?.config?.ProvisionedThroughput?.LastIncreaseDateTime,
  );
export const LastDecreaseDateTime = r =>
  MetaField(
    'Last decrease date',
    r?.config?.ProvisionedThroughput?.LastDecreaseDateTime,
    { time: 'dayjs' },
  );
export const NumberOfDecreasesToday = r =>
  MetaField(
    'Number of decreases today',
    r?.config?.ProvisionedThroughput?.NumberOfDecreasesToday,
  );
export const ReadCapacityUnits = r =>
  MetaField(
    'Read capacity units',
    r?.config?.ProvisionedThroughput?.ReadCapacityUnits,
  );
export const WriteCapacityUnits = r =>
  MetaField(
    'Write capacity units',
    r?.config?.ProvisionedThroughput?.WriteCapacityUnits,
  );
export const TableSize = r =>
  MetaField('Table size (bytes)', r?.config?.TableSizeBytes);
export const StreamsEnabled = r =>
  MetaField('Streams enabled', r?.config?.StreamSpecification?.StreamEnabled);
export const LatestStreamsARN = r =>
  MetaField('Latest streams ARN', r?.identifier?.LatestStreamArn);
export const Replicas = r => MetaField('Replicas', r?.config?.Replicas);
export const DynamoDBServerSideEncryption = r =>
  MetaField(
    `Server-side encryption`,
    r?.security?.SSEDescription?.EncryptionKeyType,
  );
export const RestoredFromBackup = r =>
  MetaField('Restored from backup', r?.config?.RestoredFromBackup);

// Lambda
export const VPCIds = r =>
  MetaField(
    'VPC Ids',
    map(r?.network?.VpcConfig, e => e.VpcId),
  );
export const LastModified = r =>
  MetaField(
    'Last modified',
    r?.config?.LastModified ||
      r?.config?.LastModifiedTime ||
      r?.config?.last_modified,
    { time: 'dayjs' },
  );
export const CodeSha256 = r => MetaField('Code SHA-256', r?.config?.CodeSha256);
export const Runtime = r => MetaField('Runtime', r?.config?.Runtime);
export const LambdaLastModified = r =>
  MetaField('Last Modified', r?.config?.LastModified, { time: 'utc' });
export const LambdaDescription = r =>
  MetaField('Description', r?.config?.Description);
export const MemorySize = r => MetaField('Memory size', r?.config?.MemorySize);
export const CodeSize = r => MetaField('Code Size', r?.config?.CodeSize);
export const Role = r => MetaField('Role', r?.config?.Role, { colSpan: 2 });
export const Timeout = r => MetaField('Timeout', r?.config?.Timeout);
export const Handler = r => MetaField('Handler', r?.config?.Handler);
export const Location = r => MetaField('Location', '~');

// EKS
export const PlatformVersion = r => MetaField('Platform Version', '~');
export const PublicCIDRs = r => MetaField('Public CIDRs', '~');
export const PrivateAccessWithinVPC = r =>
  MetaField('PrivateAccess Within VPC', '~');
export const EncryptionKeyARN = r => MetaField('Encryption Key ARN', '~');
export const Certificate = r =>
  MetaField('Certificate', r?.config?.certificateAuthority?.data);

// RedshiftCluster
export const NamespaceARN = r =>
  MetaField('Namespace ARN', r?.identifier?.ClusterNamespaceArn, {
    colSpan: 2,
  });
export const TotalSize = r =>
  MetaField('Total size (in MB)', r?.storage?.TotalStorageCapacityInMegaBytes);
export const IAMRoles = r =>
  MetaField(
    'IAM roles',
    map(r?.security?.IamRoles, o => o?.IamRoleArn),
    { colSpan: 2 },
  );
export const NodeType = r => MetaField('Node type', r?.config?.NodeType);
export const NumberOfNodes = r =>
  MetaField('Number of nodes', r?.config?.NumberOfNodes);
export const ElasticIP = r =>
  MetaField('Elastic IP', r?.network?.ElasticIpStatus?.ElasticIp);
export const EndpointURL = r =>
  MetaField(
    'Elastic URL (URL : port)',
    r?.identifier?.Endpoint?.Address + ' : ' + r?.identifier?.Endpoint?.Port,
  );
export const EndpointID = r =>
  MetaField(
    'Endpoint IDs',
    r?.identifier?.Endpoint?.VpcEndpoints?.map(o => o?.VpcEndpointId),
  );
export const RedshiftVersion = r =>
  MetaField('Version', r?.config?.ClusterVersion);
export const KMSID = r =>
  MetaField('KMS ID', r?.identifier?.KmsKeyId ?? r?.identifier?.KmsMasterKeyId);
export const AvailabilityStatus = r =>
  MetaField('Availability status', r?.config?.ClusterAvailabilityStatus);
export const AllowVersionUpgrade = r =>
  MetaField('Allow version upgrade', r?.config?.AllowVersionUpgrade);
export const SnapshotRetentionPeriod = r =>
  MetaField(
    'Snapshot retention period',
    'Automated: ' +
      r?.config?.AutomatedSnapshotRetentionPeriod +
      ', Manual: ' +
      r?.config?.ManualSnapshotRetentionPeriod,
    { colSpan: 2 },
  );
export const DatabaseName = r =>
  MetaField('Database name', r?.identifier?.DBName);
export const PublicKey = r =>
  MetaField('Public key', r?.security?.ClusterPublicKey, { colSpan: 3 });

// Group
export const Federation = r => MetaField('Federation', r?.federated);

// Kubernetes
export const EndpointPublicURL = r =>
  MetaField('Endpoint public URL', r?.config?.endpoint, { colSpan: 2 });
export const PublicAccessEnabled = r =>
  MetaField(
    'Public Access Enabled',
    r?.network?.resourcesVpcConfig?.endpointPublicAccess,
  );
export const PrivateAccessEnabled = r =>
  MetaField(
    'Private Access Enabled',
    r?.network?.resourcesVpcConfig?.endpointPrivateAccess,
  );
export const PublicAccessCIDR = r =>
  MetaField(
    'Public access CIDR',
    r?.network?.resourcesVpcConfig?.publicAccessCidrs,
  );
export const RoleARN = r =>
  MetaField(
    'Role ARN',
    r?.connection?.IAMRole ||
      r?.identifier?.RoleArn ||
      r?.identifier?.roleArn ||
      r?.identifier?.taskRoleArn,
    {
      colSpan: 2,
    },
  );
export const InterfaceSecurityGroups = r =>
  MetaField(
    'Interface security groups',
    r?.network?.resourcesVpcConfig?.securityGroupIds,
  );
export const ClusterSecurityGroups = r =>
  MetaField(
    'Cluster security group',
    r?.network?.resourcesVpcConfig?.clusterSecurityGroupId,
  );
export const IPFamily = r =>
  MetaField('IP family', r?.network?.kubernetesNetworkConfig?.ipFamily);
export const ServiceIPv4CIDR = r =>
  MetaField(
    'Service IPv4 CIDR',
    r?.network?.kubernetesNetworkConfig?.serviceIpv4Cidr,
  );
export const AttachedVMs = r =>
  MetaField(
    'Attached VMs',
    r?.instances?.map(o => {
      const items = o?.split(',');
      const name = split(items[1], ': ')?.[1];
      const id = split(items[0], ': ')?.[1];
      return {
        name: name ? name + ' (' + id + ')' : id,
        id,
      };
    }),
    {
      linkResourceType: NativeResources.Ec2Instance,
      colSpan: 2,
    },
  );

// User
export const DesignatedAdmin = r =>
  MetaField('Designated admin', r?.is_designated_admin);
export const ConsoleAccess = r =>
  MetaField('Console access', r?.has_console_access, {
    withDot: true,
    oppositeColor: true,
  });
const getPrivilegeLevel = r => {
  const value = r?.unused_services?.total;
  let level = '';
  if (value < 5) level = '< 5';
  else if (value >= 5 && value <= 25) level = '5-25';
  else if (value >= 25 && value <= 50) level = '25-50';
  else level = '> 50';
  return level + ' services';
};
export const PrivilegeLevel = r =>
  MetaField('Console access', getPrivilegeLevel(r));
export const UserGroupsAndPolicies = r =>
  MetaField(
    'Groups / Policies',
    r?.group_count + ' / ' + r?.group_policy_count,
  );
export const ResourcesOwned = r => MetaField('Resources owned', '~');
export const ConfidentialDataAccess = r =>
  MetaField('Confidential data access?', '~');
export const KMSKeysOwned = r => MetaField('KMS keys owned', '~');
export const KMSKeysUsed = r => MetaField('KMS keys used', '~');
export const GrantsGiven = r => MetaField('Grants given', '~');
export const BusinessCriticalAccess = r =>
  MetaField('Business critical access?', '~');

//ECSCluster
export const RegisteredContainers = r =>
  MetaField('Registered container', r?.config?.containerInstances?.length);
export const TaskDefinitions = r => MetaField('Task definitions', '');
export const RunningPendingTasks = r =>
  MetaField(
    'Running / Pending tasks',
    r?.compute?.runningTasksCount + ' / ' + r?.compute?.pendingTasksCount,
  );

//ECSCluster
export const ECSServices = r => MetaField('Services', '');
export const ClusterArn = r =>
  MetaField('Cluster ARN', r?.identifier?.clusterArn, { colSpan: 2 });
export const ServiceArn = r => MetaField('Service ARN', '');
export const ECSPlatformVersion = r => MetaField('Platform version', '');
export const ECSPlatformFamily = r => MetaField('Platform family', '');
export const PendingCount = r =>
  MetaField('Pending count', r?.compute?.pendingCount);
export const DesiredCount = r =>
  MetaField('Desired count', r?.compute?.desiredCount);
export const RunningCount = r =>
  MetaField('Running count', r?.compute?.runningCount);
export const RunningPendingCount = r =>
  MetaField(
    'Running / Pending count',
    r?.compute?.runningCount + ' / ' + r?.compute?.pendingCount,
  );
export const NetworkConfiguration = r => MetaField('Network configuration', '');
export const Subnets = r =>
  MetaField('Subnets', r?.connection?.Subnet ?? r?.config?.SubnetIds, {
    linkResourceType: NativeResources.Subnet,
  });
export const LoadBalancers = r => MetaField('Load balancers', '');
export const ServiceRegistries = r => MetaField('Service registries', '');
/* eslint-enable */

//ECS Service
export const AssignPublicIP = r =>
  MetaField(
    'Assign public IP',
    r?.network?.networkConfiguration.awsvpcConfiguration.assignPublicIp,
  );
export const DeploymentController = r =>
  MetaField('Deployment controller', r?.config?.deploymentController?.type);

//ECS Task Definition
export const NetworkMode = r =>
  MetaField('Network mode', r?.network?.networkMode);
export const IPCMode = r => MetaField('IPC mode', r?.config?.ipcMode);
export const PIDMode = r => MetaField('PID mode', r?.config?.pidMode);
export const ExecutionRoleARN = r =>
  MetaField('Execution role ARN', r?.identifier?.executionRoleArn, {
    colSpan: 2,
  });
export const Family = r => MetaField('Family', r?.config?.family);
export const Revision = r => MetaField('Revision', r?.config?.revision);
export const DNSServers = r => MetaField('DNS servers', r?.dnsServers);
export const ReadOnlyRootFilesystem = r =>
  MetaField('Read-only root filesystem', r?.readonlyRootFilesystem);
export const InitProcessEnabled = r =>
  MetaField('Init process enabled', r?.linuxParameters?.initProcessEnabled);
export const Condition = r => MetaField('Condition', r?.dependsOn?.condition);
export const Secrets = r => MetaField('Secrets', r?.secrets, { colSpan: 2 });
export const Environment = r => MetaField('Environment', '', { colSpan: 4 });
export const MountPoints = r =>
  MetaField(
    'Mount points',
    r?.mountPoints?.map(
      o => o?.sourceVolume + ': ' + (o?.readOnly ? 'Yes' : 'No'),
    ),
  );
export const HealthCheck = r => MetaField('Health check', r?.command);

//ECS Task
export const LaunchType = r =>
  MetaField('Launch type', r?.config?.launchType || r?.compute?.launchType);
export const DesiredStatus = r =>
  MetaField('Desired status', r?.compute?.desiredStatus);
export const HealthStatus = r =>
  MetaField('Health status', r?.compute?.healthStatus);
export const LastStatus = r => MetaField('Last status', r?.compute?.lastStatus);
export const Group = r => MetaField('Group', r?.config?.group);
export const ECSTaskStorage = r =>
  MetaField('Storage', r?.config?.ephemeralStorage);
export const ECSTaskAllocation = r =>
  MetaField(
    'Allocation',
    r?.config?.cpu + ' CPU units, ' + r?.config?.memory + ' MiB',
  );
export const Connectivity = r =>
  MetaField('Connectivity', r?.config?.connectivity);
export const TaskDefinitionARN = r =>
  MetaField(
    'Task definition ARN',
    r?.identifier?.taskDefinitionArn || r?.config?.taskDefinition,
    {
      colSpan: 2,
    },
  );
export const PlatformFamilyAndVersion = r =>
  MetaField(
    'Platform family / version',
    (r?.config?.platformFamily ?? '-') +
      ' / ' +
      (r?.config?.platformVersion ?? '-'),
  );
export const DesiredAndRunningCount = r =>
  MetaField(
    'Desired / Running count',
    (r?.config?.desiredCount || 0) + ' / ' + (r?.config?.runningCount || 0),
  );

//ECS Container Instance
export const VirtualMachineInstanceID = r =>
  MetaField('Virtual machine instance ID', r?.identifier?.ec2InstanceId, {
    linkResourceType: NativeResources.Ec2Instance,
  });
export const CapacityProviderName = r =>
  MetaField('Capacity provider name', r?.identifier?.capacityProviderName);
export const Allocation = r =>
  MetaField(
    'Allocation',
    find(r?.config?.registeredResources, o => o.name === 'CPU')?.integerValue +
      ' CPU units, ' +
      find(r?.config?.registeredResources, o => o.name === 'MEMORY')
        ?.integerValue +
      ' MiB',
  );
export const Ports = r =>
  MetaField(
    'Ports',
    find(
      r?.config?.registeredResources,
      o => o.name === 'PORTS',
    )?.stringSetValue?.join(', '),
  );
export const AgentConnected = r =>
  MetaField('Agent connected', r?.config?.agentConnected);
export const Agent = r =>
  MetaField(
    'Agent (version / hash)',
    r?.config?.versionInfo?.agentVersion +
      ' / ' +
      r?.config?.versionInfo?.agentHash,
  );
export const DockerVersion = r =>
  MetaField('Docker version', r?.config?.versionInfo?.dockerVersion);
export const RegisteredAt = r =>
  MetaField('Registered at', r?.config?.registeredAt, { time: 'dayjs' });

//Container
export const ContainerID = r => MetaField('Container ID', r?.container_id);
export const DeploymentRegion = r => MetaField('Deployment region', r?.region);
export const DeploymentVPC = r => MetaField('Deployment VPC', r?.vpc);
export const DeploymentType = r => MetaField('Deployment Type', '');
export const DeployedOn = r => MetaField('Deployed on', '');
export const DeploymentID = r => MetaField('Deployment ID', '');
export const Namespace = r => MetaField('Namespace', r?.config?.namespace);
export const Pod = r => MetaField('Pod', r?.config?.pod);
export const IpAddress = r => MetaField('IP address', '');
export const Technologies = r => MetaField('Technologies', '');

//Elastic Container Repository
export const RegistryID = r =>
  MetaField('Registry ID', r?.identifier?.registryId);
export const ImageId = r => MetaField('Image', '');
export const ScanOnPush = r =>
  MetaField('Scan on push', r?.config?.imageScanningConfiguration?.scanOnPush, {
    enableLabel: true,
  });
export const ImageTagMutability = r =>
  MetaField('Image tag mutability', r?.config?.imageTagMutability);
export const EncryptionType = r =>
  MetaField(
    'Encryption type',
    r?.security?.encryptionConfiguration?.encryptionType,
  );
export const ContainerImages = r =>
  MetaField('Container images', r?.repository_image_count);

//Container

//ECR Image
export const PushedAt = r =>
  MetaField('Pushed at', r?.config?.imagePushedAt, { time: 'dayjs' });
export const RepositoryName = r => MetaField('Repository name', '');
export const ImageManifestMediaType = r =>
  MetaField('Image manifest media type', r?.config?.imageManifestMediaType);
export const ImageSize = r =>
  MetaField('Image size (in Bytes)', r?.storage?.imageSizeInBytes);
export const ImageDigest = r =>
  MetaField('Image digest', r?.identifier?.imageDigest, { colSpan: 2 });
export const ImageScanStatus = r =>
  MetaField('Image scan status', r?.compute?.imageScanStatus?.status);
export const FindingsSeverityCount = r => {
  const counts = r?.compute?.imageScanFindingsSummary?.findingSeverityCounts;
  return MetaField(
    'Findings severity count',
    counts ? sum(Object.values(counts)) : undefined,
  );
};
export const LastRecordedPullTime = r =>
  MetaField('Last recorded pull time', r?.config?.lastRecordedPullTime, {
    time: 'dayjs',
  });
export const ArtifactMediaType = r =>
  MetaField('Artifact media type', r?.config?.artifactMediaType);

//ElastiCache
export const ElastiCacheEngine = r =>
  MetaField(
    'Engine',
    r?.config?.Engine + ' (' + r?.config?.EngineVersion + ')',
  );
export const PreferredAZ = r =>
  MetaField('Preferred AZ', r?.config?.PreferredAvailabilityZone);
export const ParameterGroups = r =>
  MetaField(
    'Parameter group',
    r?.config?.CacheParameterGroup?.CacheParameterGroupName,
  );
export const ReplicationGroupID = r =>
  MetaField('Replication group ID', r?.identifier?.ReplicationGroupId);
export const SnapshotWindow = r =>
  MetaField('Snapshot window', r?.config?.SnapshotWindow);
export const SnapshotRetention = r =>
  MetaField('Snapshot retention', r?.config?.SnapshotRetentionLimit);
export const TopicStatus = r =>
  MetaField('Topic status', r?.config?.NotificationConfiguration?.TopicStatus);
export const AuthToken = r =>
  MetaField('Auth token enabled', r?.security?.AuthTokenEnabled);
export const EncryptInTransit = r =>
  MetaField('Encrypt in transit', r?.security?.TransitEncryptionEnabled);
export const EncryptAtRest = r =>
  MetaField('Encrypt at rest', r?.security?.AtRestEncryptionEnabled);
export const PreferredMaintenanceWindow = r =>
  MetaField(
    'Preferred maintenance window',
    r?.config?.PreferredMaintenanceWindow,
  );
export const AutoMinorVersionUpgrade = r =>
  MetaField('Auto minor version upgrade', r?.config?.AutoMinorVersionUpgrade);

//ELB
export const Scheme = r => MetaField('Scheme', r?.config?.Scheme);
export const Instances = r =>
  MetaField(
    'Instances',
    map(r?.compute?.Instances, o => o?.InstanceId),
  );
export const SourceSecurityGroup = r =>
  MetaField(
    'Source security group',
    r?.network?.SourceSecurityGroup?.GroupName,
  );

//ElasticMapReduce
export const OSReleaseLabel = r =>
  MetaField('OS release label', r?.config?.ReleaseLabel);
export const LogURI = r => MetaField('Log URI', r?.config?.LogUri);
export const EC2Instances = r =>
  MetaField(
    'EC2 instances',
    r?.compute?.Instances?.map(o => o?.Ec2InstanceId),
    {
      linkResourceType: NativeResources.Ec2Instance,
    },
  );
export const CustomAMIID = r =>
  MetaField('Custom AMI ID', r?.identifier?.CustomAmiId);
export const ServiceRole = r =>
  MetaField('Service role', r?.identifier?.ServiceRole);
export const TerminationProtected = r =>
  MetaField('Termination protected', r?.config?.TerminationProtected);
export const Applications = r =>
  MetaField(
    'Applications',
    r?.config?.Applications?.map(o => o?.Name + '/' + o?.Version),
  );
export const VisibleToAllUsers = r =>
  MetaField('Visible to all users', r?.config?.VisibleToAllUsers);
export const MasterDNSName = r =>
  MetaField('Master DNS name', r?.identifier?.MasterPublicDnsName);
export const AutoTerminate = r =>
  MetaField('Auto terminate', r?.config?.AutoTerminate);
export const IAMInstanceProfile = r =>
  MetaField(
    'IAM instance profile',
    r?.compute?.Ec2InstanceAttributes?.IamInstanceProfile,
  );
export const MasterSecurityGroup = r =>
  MetaField(
    'Master security group',
    r?.compute?.Ec2InstanceAttributes?.EmrManagedMasterSecurityGroup,
  );
export const SlaveSecurityGroup = r =>
  MetaField(
    'Slave security group',
    r?.compute?.Ec2InstanceAttributes?.EmrManagedSlaveSecurityGroup,
  );
export const ServiceAccessSecurityGroup = r =>
  MetaField(
    'Service access security group',
    r?.compute?.Ec2InstanceAttributes?.ServiceAccessSecurityGroup,
  );

//Virtual Private Gateway
export const TypeOfVPNConnection = r =>
  MetaField('Type of VPN connection', '~');
export const VPCAttachments = r => MetaField('VPC attachments', '~');

//API Gateway
export const EndpointType = r =>
  MetaField('Endpoint type', r?.config?.endpointConfiguration?.types);
export const CrossOriginResourceSharing = r =>
  MetaField('Cross-origin resource sharing', '~');
export const Proxy = r => MetaField('Proxy', r?.config?.proxy);
export const IntegrationType = r =>
  MetaField('Integration type', r?.methodIntegration?.type);
export const UseDefaultTimeOut = r =>
  MetaField('Use default time out', r?.methodIntegration?.timeoutInMillis);
export const APIGatewayEndpoint = r =>
  MetaField('Endpoint', r?.methodIntegration?.uri, { colSpan: 2 });
export const Authorization = r =>
  MetaField('Authorization', r?.authorizationType);
export const RequestValidator = r =>
  MetaField('Request validator', r?.requestValidatorId);
export const APIKeyRequired = r =>
  MetaField('API key required', r?.apiKeyRequired);
export const DetailedMetrics = r =>
  MetaField('Detailed metrics', r?.metricsEnabled);
export const ClientCertificates = r =>
  MetaField(
    'Client certificates',
    r?.config?.stages?.map(o => o?.clientCertificateId),
  );

//Snapshot
export const SnapshotDescription = r =>
  MetaField('Description', r?.config?.Description, { colSpan: 2 });
export const OwnerAlias = r => MetaField('Owner alias', r?.config?.OwnerAlias);
export const VolumeID = r => MetaField('Volume ID', r?.identifier?.VolumeId);
export const VolumeSize = r => MetaField('Volume size (GiB)', r?.volume_size);
export const EncryptedKeyID = r =>
  MetaField('Encryption key ID', r?.identifier?.DataEncryptionKeyId);
export const KMSKeyID = r =>
  MetaField(
    'KMS key ID',
    r?.identifier?.KmsKeyId ||
      r?.identifier?.KmsMasterKeyId ||
      r?.security?.encryptionConfiguration?.kmsKey,
  );
export const SnapshotProgress = r => MetaField('Progress', r?.config?.Progress);
export const StorageTier = r => MetaField('Storage tier', r?.storage_tier);
export const DBInstanceID = r =>
  MetaField('DB instance ID', r?.identifier?.DBInstanceIdentifier);
export const DBEngine = r =>
  MetaField(
    'DB Engine (type / version)',
    r?.config?.Engine + ' / ' + r?.config?.EngineVersion,
  );
export const SnapshotType = r =>
  MetaField('Snapshot type', r?.config?.SnapshotType);
export const Storage = r =>
  MetaField(
    'Storage (type / size)',
    r?.storage?.StorageType + ' / ' + r?.storage?.AllocatedStorage,
  );
export const Port = r => MetaField('Port', r?.network?.Port);
export const IAMDBAuthentication = r =>
  MetaField(
    'IAM DB authentication',
    r?.security?.IAMDatabaseAuthenticationEnabled,
  );

//Secret
export const RotationEnabled = r =>
  MetaField('Rotation enabled', r?.config?.RotationEnabled);
export const LastRotationDate = r =>
  MetaField('Last rotation date', r?.config?.LastRotatedDate);
export const RotationFunction = r =>
  MetaField('Rotation function (Lambda ARN)', r?.config?.RotationLambdaARN, {
    colSpan: 2,
  });
export const RotationRules = r =>
  MetaField(
    'Rotation rules (Automatically after days)',
    r?.config?.RotationRules?.AutomaticallyAfterDays,
  );
export const LastChangeDate = r =>
  MetaField(
    'Last change date',
    `${dayjs(r?.config?.LastChangedDate).format('llll')}`,
  );
export const LastAccessedDate = r =>
  MetaField('Last accessed date', r?.config?.LastAccessedDate, {
    time: 'dayjs',
  });
export const DeleteDate = r =>
  MetaField('Delete date', r?.config?.DeletedDate, { time: 'dayjs' });
export const ReplicationStatus = r => MetaField('Replication status', '~');
export const VersionIDCurrent = r =>
  MetaField('Version ID – Current', r?.config?.CurrentVersionIds);
export const VersionIDPrevious = r =>
  MetaField('Version ID – Previous', r?.config?.PreviousVersionIds);
export const VersionIDPending = r =>
  MetaField('Version ID – Pending', r?.config?.PendingVersionIds);

//ACM Certificate
export const Issuer = r => MetaField('Issuer', r?.config?.Issuer);
export const IssuedAt = r => MetaField('Issued at', r?.config?.IssuedAt);
export const KeyAlgorithm = r =>
  MetaField('Key algorithm', r?.security?.KeyAlgorithm);
export const Serial = r =>
  MetaField('Serial', r?.identifier?.Serial, { colSpan: 2 });
export const Subject = r => MetaField('Subject', r?.config?.Subject);
export const CertificateType = r =>
  MetaField('Type', r?.config?.Type || r?.config?.CertificateType);
export const AcmCertificateUsedBy = r => MetaField('Used by', '~');
export const CertificateAuthority = r =>
  MetaField('Certificate authority', '~');
export const KeyUsages = r =>
  MetaField(
    'Key usages',
    r?.config?.KeyUsages?.map(o => o.Name),
  );
export const RenewalStatus = r =>
  MetaField('Renewal status', r?.config?.RenewalEligibility);
export const AcmCertificateUpdatedAt = r => MetaField('Updated at', '~');
export const SignatureAlgorithm = r =>
  MetaField('Signature algorithm', r?.security?.SignatureAlgorithm);

//RDS Certificate
export const Thumbprint = r => MetaField('Thumbprint', r?.config?.Thumbprint);
export const Validity = r =>
  MetaField('Validity', `${r?.config?.ValidFrom} - ${r?.config?.ValidTill}`);
export const CustomerOverride = r =>
  MetaField('Customer override', r?.config?.CustomerOverride);
export const CustomerOverrideExpiry = r =>
  MetaField('Customer override expiry', r?.config?.CustomerOverrideValidTill);

//WAF
export const ManagedByFirewallManager = r =>
  MetaField('Managed by firewall manager', r?.config?.ManagedByFirewallManager);
export const ManagedRuleGroups = r =>
  MetaField(
    'Managed rule groups',
    r?.config?.ManagedRuleGroups?.map(o => o?.Name),
  );
export const RuleGroups = r =>
  MetaField(
    'Rule groups',
    r?.config?.RuleGroups?.map(o => o?.Name),
  );
export const WebACLs = r => MetaField('Web ACLs', '~');
export const WafRules = r => MetaField('Rules', r?.config?.Rules?.length);
export const WafV2Rules = r =>
  MetaField(
    'Rules',
    r?.config?.Rules?.map(o => o?.Name),
  );

//Cloud Front
export const DomainName = r => MetaField('Domain name', r?.network?.DomainName);
export const WAFACLID = r =>
  MetaField('WAF ACL ID', r?.identifier?.WebACLId, { colSpan: 2 });
export const HTTPVersion = r =>
  MetaField('HTTP version', r?.network?.HttpVersion);
export const DefaultRoot = r =>
  MetaField('Default root', r?.DefaultRootObject?.items);
export const CloudFrontAliases = r =>
  MetaField('Aliases', r?.config?.DistributionConfig?.Aliases?.items);
export const OriginGroups = r =>
  MetaField('Origin groups', r?.config?.OriginGroups?.Quantity);
export const OriginGroupID = r =>
  MetaField(
    'Origin group ID',
    map(r?.config?.OriginGroups?.items, o => o?.Id),
  );
export const OriginGroupEntities = r =>
  MetaField(
    'Origin group entities',
    map(r?.config?.OriginGroups?.items, o => o?.OriginId),
  );
export const GeoRestrictions = r =>
  MetaField(
    'Geo restrictions',
    !!r?.config?.Restrictions?.GeoRestriction?.Quantity ? 'Yes' : 'Not enabled',
  );
export const RestrictionType = r =>
  MetaField(
    'Restriction type',
    r?.config?.Restrictions?.GeoRestriction?.RestrictionType,
  );
export const RestrictedGeos = r =>
  MetaField('Restricted Geos', r?.config?.Restrictions?.GeoRestriction?.items);
export const IPv6Enabled = r =>
  MetaField('IPv6 enabled', r?.network?.IsIPV6Enabled);
export const CloudFrontLogging = r =>
  MetaField('Logging', r?.config?.Logging?.Bucket);
export const Stage = r => MetaField('Stage', '~');
export const Compress = r =>
  MetaField('Compress', r?.config?.DefaultCacheBehavior?.Compress);

//sagemaker
export const PlatformID = r =>
  MetaField('Platform ID', r?.identifier?.PlatformIdentifier);
export const Url = r =>
  MetaField('URL', r?.config?.Url || r?.identifier?.repositoryUri);
export const VolumeSizeInGB = r =>
  MetaField('Volume size (in GB)', r?.storage?.VolumeSizeInGB);
export const DefaultRepository = r =>
  MetaField('Default repository', r?.config?.DefaultCodeRepository);
export const RootAccess = r => MetaField('Root access', r?.config?.RootAccess);
export const MetaDataConfiguration = r =>
  MetaField(
    'Meta data configuration',
    r?.config?.InstanceMetadataServiceConfiguration,
  );

//Transit gateway
export const TransitGatewayVPCIds = r =>
  MetaField(
    'VPC Ids',
    map(r?.network?.TransitGatewayVpcAttachments, e => e.VpcId),
  );
export const AmazonSideASN = r =>
  MetaField('Amazon side ASN', r?.config?.Options?.AmazonSideAsn);
export const AutoAcceptSharedAttachments = r =>
  MetaField(
    'Auto accept shared attachments',
    r?.config?.Options?.AutoAcceptSharedAttachments,
  );
export const DefaultRouteTableAssociation = r =>
  MetaField(
    'Default route table association',
    r?.config?.Options?.DefaultRouteTableAssociation,
  );
export const DefaultRouteTableID = r =>
  MetaField(
    'Default route table ID',
    r?.config?.Options?.AssociationDefaultRouteTableId,
  );
export const DefaultRouteTablePropagation = r =>
  MetaField(
    'Default route table propagation',
    r?.config?.Options?.DefaultRouteTablePropagation,
  );
export const VPNECMPSupport = r =>
  MetaField('VPN ECMP support', r?.config?.Options?.VpnEcmpSupport);
export const DNSSupport = r =>
  MetaField(
    'DNS support',
    r?.config?.Options?.DnsSupport ??
      (r?.config?.DnsSupport === 'enable' ? 'Enabled' : 'Disabled'),
  );

//NAT gateway
export const ConnectivityType = r =>
  MetaField('Connectivity type', r?.network?.ConnectivityType);

//Transit gateway attachment
export const TransitGatewayID = r =>
  MetaField('Transit gateway ID', r?.transit_gateway_attachment_id);
export const TransitGatewayOwnerID = r =>
  MetaField('Transit gateway owner ID', r?.transit_gateway_owner_id);
export const VPCOwnerID = r => MetaField('VPC owner ID', r?.config?.VpcOwnerId);
export const IPv6Support = r =>
  MetaField(
    'IPv6 support',
    r?.config?.Ipv6Support === 'enable' ? 'Enabled' : 'Disabled',
  );

//Route table
export const MainRouteTable = r =>
  MetaField(
    'Main route table',
    r?.config?.MainRouteTable ||
      r?.config?.Associations?.find(o => o?.Main)?.RouteTableId,
  );
export const RouteTableSubnetID = r =>
  MetaField(
    'Subnet IDs',
    r?.config?.SubnetIds || r?.config?.Associations?.map(o => o?.SubnetId),
    { linkResourceType: NativeResources.Subnet },
  );

//Route
export const DestinationCIDRBlock = r =>
  MetaField('Destination CIDR block', '~');
export const DestinationPrefixID = r => MetaField('Destination prefix ID', '~');
export const EgressOnlyInternetGatewayID = r =>
  MetaField('Egress only internet gateway ID', '~');
export const GatewayID = r => MetaField('Gateway ID', '~');
export const NATInstanceID = r => MetaField('NAT instance ID', '~');
export const NATGatewayID = r => MetaField('NAT gateway ID', '~');
export const RouteTransitGatewayID = r => MetaField('Transit gateway ID', '~');
export const LocalGatewayID = r => MetaField('Local gateway ID', '~');
export const NetworkInterfaceID = r => MetaField('Network interface ID', '~');
export const RouteCreatedBy = r => MetaField('Route created by', '~');
export const VPCPeeringID = r => MetaField('VPC peering ID', '~');
export const CoreNetworkARN = r => MetaField('core network ARN', '~');

//Okta User
export const TransitioningToStatus = r =>
  MetaField('Transitioning to status', r?.config?.transitioningToStatus);
export const Login = r => MetaField('Login', r?.config?.login);
export const FullName = r => MetaField('Full name', r?.config?.fullName);
export const DisplayName = r =>
  MetaField(
    'Display name',
    r?.identifier?.displayName || r?.config?.display_name,
  );
export const SecondaryEmail = r =>
  MetaField('Secondary email', r?.config?.secondEmail || r?.config?.otherMails);
export const ProfileURL = r => MetaField('Profile URL', r?.config?.profileUrl);
export const UserType = r => MetaField('User type', r?.config?.userType);
export const OktaOrganization = r =>
  MetaField('Organization', r?.config?.organization);
export const Division = r => MetaField('Division', r?.config?.division);
export const Title = r =>
  MetaField('Title', r?.config?.title || r?.config?.jobTitle);
export const Department = r => MetaField('Department', r?.config?.department);
export const CostCenter = r => MetaField('Cost center', r?.config?.costCenter);
export const EmployeeNumber = r =>
  MetaField('Employee number', r?.config?.employeeNumber);
export const ActivatedAt = r =>
  MetaField('Activated at', r?.config?.activated, { time: 'utc' });
export const LastLogin = r =>
  MetaField('Last login', r?.config?.lastLogin, { time: 'utc' });
export const PasswordChangedAt = r =>
  MetaField(
    'Password changed at',
    r?.config?.passwordChanged || r?.config?.las,
    { time: 'utc' },
  );
export const StateChangedAt = r =>
  MetaField('State changed at', r?.config?.statusChanged, { time: 'utc' });
export const PasswordPolicies = r =>
  MetaField('Password policies', r?.config?.passwordPolicies);
export const OktaDomains = r =>
  MetaField('Okta domains', r?.connection?.OktaDomain, {
    linkResourceType: NativeResources.OktaDomain,
  });

//Okta Group
export const OktaGroupType = r => MetaField('Type', r?.config?.type);
export const ID = r => MetaField('ID', r?.resource_id ?? r?.identifier?.id);
export const DistinguishedName = r =>
  MetaField('Distinguished name', r?.config?.dn);
export const WindowsDomainQualifiedName = r =>
  MetaField(
    'Windows domain qualified name',
    r?.config?.windowsDomainQualifiedName,
  );
export const ExternalID = r => MetaField('External ID', r?.config?.externalId);
export const SAMAccountName = r =>
  MetaField('SAM account name', r?.config?.samAccountName);
export const ObjectSid = r => MetaField('Object Sid', r?.config?.objectSid);
export const ObjectClass = r =>
  MetaField('Object class', r?.config?.objectClass);
export const GroupMembers = r =>
  MetaField(
    'Group members',
    r?.config?.members?.map(o => o?.id),
    { linkResourceType: NativeResources.OktaUser },
  );
export const LastMemberUpdated = r =>
  MetaField('Last member updated', r?.config?.lastMembershipUpdated, {
    time: 'utc',
  });

//Okta Domain
export const OktaDomain = r => MetaField('Domain', r?.identifier?.domain);
export const ValidatedStatus = r =>
  MetaField('Validated status', r?.config?.validationStatus);
export const CertificateSourceType = r =>
  MetaField('Certificate source type', r?.config?.certificateSourceType);

//Okta Organization
export const CompanyName = r =>
  MetaField('Company name', r?.identifier?.companyName);
export const OktaSubDomain = r => MetaField('Sub-domain', r?.config?.subdomain);
export const Country = r => MetaField('Country', r?.config?.country);
export const Website = r => MetaField('Website', r?.config?.website);
export const ExpiresAt = r =>
  MetaField('Expires at', r?.config?.expiresAt, { time: 'utc' });

//AzureAd User
export const UserPrincipalName = r =>
  MetaField('User principal name', r?.config?.userPrincipalName);
export const SignInName = r => MetaField('Sign-in name', '~');
export const AccountEnabled = r =>
  MetaField('Account enabled', r?.config?.accountEnabled);
export const AzureAdUsername = r => MetaField('Username', '~');
export const AzureAdName = r => MetaField('Name', r?.identifier?.name);
export const MFAPhoneNumber = r =>
  MetaField('MFA phone number', r?.config?.mfaPhoneNumber);
export const NetID = r => MetaField('Net ID', '~');
export const CreationType = r =>
  MetaField('Creation type', r?.config?.creationType);

//AzureAd group
export const MembershipType = r => MetaField('Membership type', '~');
export const Owners = r => MetaField('Owners', r?.config?.owners?.length);
export const UserMembers = r =>
  MetaField('User members', r?.config?.member_users?.length);

//AzureAd Tenant
export const OrganizationName = r => MetaField('Organization name', '~');
export const OrganizationID = r => MetaField('Organization ID', '~');
export const InitialDomainName = r => MetaField('Initial domain name', '~');

//Route53 Hosted Zone
export const CallerReference = r =>
  MetaField('Caller reference', r?.identifier?.CallerReference);
export const DelegationSetID = r => MetaField('ID', '');
export const NameServers = r =>
  MetaField('Name Servers', r?.config?.DelegationSet?.NameServers);
export const PrivateZone = r =>
  MetaField('Private zone', r?.config?.Config?.PrivateZone);
export const Route53HostedZoneComment = r =>
  MetaField('Comment', r?.config?.Config?.Comment, { colSpan: 2 });
export const RecordSetCount = r =>
  MetaField('Record set count', r?.config?.ResourceRecordSetCount);
export const LinkedServices = r =>
  MetaField('Linked services', r?.config?.LinkedService?.ServicePrincipal);
export const VPCs = r => MetaField('VPCs', '~');
export const ResourceRecordSets = r => MetaField('Resource record sets', '~');
export const TrafficPolicies = r =>
  MetaField('Traffic policies', r?.config?.TrafficPolicyInstances);

//Route53 Record Set
export const GeoLocation = r => MetaField('Geo Location', '~');
export const CountryCode = r => MetaField('Country code', '~');
export const SetIdentities = r => MetaField('Set identities', '~');
export const Weight = r => MetaField('Weight', '~');
export const Failover = r => MetaField('Failover', '~');
export const MultiValueAnswer = r => MetaField('Multi value answer', '~');
export const TTL = r => MetaField('TTL', '~');
export const DNSName = r => MetaField('DNS name', r?.identifier?.DNSName);
export const HealthCheckID = r => MetaField('Health check ID', '~');
export const TrafficPolicyInstanceID = r =>
  MetaField('Traffic policy instance ID', '~');
export const IsTruncated = r => MetaField('Is truncated', '~');

//Route53 Traffic Policy
export const ServicePrincipal = r => MetaField('Service principal', '~');

//Route53 Health check
export const Route53HealthCheckEndpoint = r =>
  MetaField(
    'Endpoint',
    r?.config?.HealthCheckConfig?.IPAddress +
      ':' +
      r?.config?.HealthCheckConfig?.Port,
  );
export const ResourcePath = r =>
  MetaField('Resource path', r?.config?.HealthCheckConfig?.ResourcePath);
export const FQDN = r =>
  MetaField('FQDN', r?.config?.HealthCheckConfig?.FullyQualifiedDomainName);
export const SearchString = r =>
  MetaField('Search string', r?.config?.HealthCheckConfig?.SearchString);
export const RequestInterval = r =>
  MetaField('Request interval', r?.config?.HealthCheckConfig?.RequestInterval);
export const FailureThreshold = r =>
  MetaField(
    'Failure threshold',
    r?.config?.HealthCheckConfig?.FailureThreshold,
  );
export const MeasureLatency = r =>
  MetaField('Measure latency', r?.config?.HealthCheckConfig?.MeasureLatency);
export const Disabled = r =>
  MetaField('Disabled', r?.config?.HealthCheckConfig?.Disabled);
export const Inverted = r =>
  MetaField('Inverted', r?.config?.HealthCheckConfig?.Inverted);
export const HealthThreshold = r =>
  MetaField('Health threshold', r?.config?.HealthCheckConfig?.HealthThreshold);
export const EnableSNI = r =>
  MetaField('Enable SNI', r?.config?.HealthCheckConfig?.EnableSNI);

//Key pair
export const Fingerprint = r =>
  MetaField('Fingerprint', r?.config?.KeyFingerprint, { colSpan: 2 });
export const AssociatedVMs = r =>
  MetaField(
    'Associated VMs',
    r?.compute?.Instances?.map(o => o?.InstanceId),
    { linkResourceType: NativeResources.Ec2Instance },
  );

//Auto Scaling Group
export const LaunchTemplateName = r =>
  MetaField(
    'Launch template name',
    r?.config?.LaunchTemplate?.LaunchTemplateName,
  );
export const LaunchTemplateID = r =>
  MetaField('Launch template ID', r?.config?.LaunchTemplate?.LaunchTemplateId);
export const MinimumMaximumSize = r =>
  MetaField(
    'Minimum / Maximum size',
    r?.config?.MinSize + ' / ' + r?.config?.MaxSize,
  );
export const DesiredCapacity = r =>
  MetaField('Desired capacity', r?.config?.DesiredCapacity);
export const DefaultCoolDown = r =>
  MetaField('Default cool down', r?.config?.DefaultCooldown);
export const AsgLoadBalancers = r =>
  MetaField('Load balancers', r?.config?.LoadBalancers);
export const VPCZoneID = r =>
  MetaField('VPC zone ID', r?.identifier?.VPCZoneIdentifier);
export const TargetGroupARNs = r =>
  MetaField('Target group ARNs', r?.identifier?.TargetGroupARNs);
export const HealthCheckType = r =>
  MetaField('Health check type', r?.config?.HealthCheckType);
export const HealthCheckGracePeriod = r =>
  MetaField('Health check grace period', r?.config?.HealthCheckGracePeriod);
export const NewInstancesProtectedFromScale = r =>
  MetaField(
    'New instances protected from scale',
    r?.config?.NewInstancesProtectedFromScaleIn,
  );
export const ServiceLinkedRoleARN = r =>
  MetaField('Service linked role ARN', r?.identifier?.ServiceLinkedRoleARN);

//Prefix List
export const PrefixListID = r =>
  MetaField('Prefix list ID', r?.resource_id, { colSpan: 2 });
export const ResourceAssociations = r =>
  MetaField(
    'Resource associations',
    r?.config?.ResourceAssociations?.map(o => o?.ResourceId),
  );
export const AddressFamily = r =>
  MetaField('Address family', r?.network?.AddressFamily);

//DHCP Options
export const DHCPOptionsID = r =>
  MetaField('DHCP options ID', r?.resource_id, { colSpan: 2 });

//IAM Group
export const Users = r => MetaField('Users', r?.attached_user_names);
export const IAMState = r =>
  MetaField('State', r?.user_count > 0 ? 'Attached' : 'Unattached');
export const IAMGroupAttachedPolicies = r =>
  MetaField(
    'Attached policies',
    r?.config?.GroupAttachedPolicies?.map(o => o?.PolicyName),
  );

//Document DB
export const HostedZoneID = r =>
  MetaField('Hosted zone ID', r?.idetifier?.Endpoint?.HostedZoneId);
export const EndpointAddress = r =>
  MetaField('Endpoint address', r?.idetifier?.Endpoint?.Address);
export const EndpointPort = r =>
  MetaField('Endpoint port', r?.idetifier?.Endpoint?.Port);
export const Engine = r =>
  MetaField('Engine', r?.config?.Engine + ' ' + r?.config?.EngineVersion);
export const Cluster = r =>
  MetaField('Cluster', r?.identifier?.DBClusterIdentifier);
export const CaCertificateID = r =>
  MetaField('CA certificate ID', r?.identifier?.CACertificateIdentifier);

//Elastic Container Registry
export const Repositories = r => MetaField('Repositories', '~');

//Neptune
export const License = r => MetaField('License', r?.config?.LicenseModel);
export const NeptuneEndpoint = r =>
  MetaField('Endpoint', r?.identifier?.Endpoint?.Address, { colSpan: 2 });
export const NeptuneClusterEndpoint = r =>
  MetaField('Endpoint', r?.config?.ReaderEndpoint);
export const ReaderEndpoint = r =>
  MetaField('Reader endpoint', r?.config?.ReaderEndpoint);
export const ListenPort = r =>
  MetaField('Listen port', r?.identifier?.Endpoint?.Port);
export const BackupRetentionDays = r =>
  MetaField('Backup retention (days)', r?.config?.BackupRetentionPeriod);
export const PerformanceInsights = r =>
  MetaField('Performance insights', r?.config?.PerformanceInsightsEnabled);
export const MonitoringARN = r =>
  MetaField('Monitoring ARN', r?.identifier?.MonitoringRoleARN);
export const ParameterGroup = r =>
  MetaField('Parameter group', r?.config?.DBClusterParameterGroup);
export const SubnetGroup = r =>
  MetaField('Subnet group', r?.network?.DBSubnetGroup);
export const NeptuneClusterInstances = r =>
  MetaField(
    'Instances',
    r?.security?.DBClusterMembers?.map(o => o?.DBInstanceIdentifier),
  );
export const AssociatedRoles = r =>
  MetaField(
    'Associated roles',
    r?.security?.AssociatedRoles?.map(o => o?.RoleArn),
  );
export const CrossAccountClone = r =>
  MetaField('Cross account clone', r?.config?.CrossAccountClone);

//Cloud Trail
export const LogFileValidation = r =>
  MetaField('Log file validation', r?.config?.LogFileValidationEnabled);
export const CloudWatchLogGroupARN = r =>
  MetaField(
    'CloudWatch log group ARN',
    r?.identifier?.CloudWatchLogsLogGroupArn,
  );
export const CloudWatchLogRoleARN = r =>
  MetaField('CloudWatch log role ARN', r?.identifier?.CloudWatchLogsRoleArn);
export const SNSTopicName = r =>
  MetaField('SNS Topic name', r?.identifier?.SnsTopicName);
export const SNSTopicARN = r =>
  MetaField('SNS Topic ARN', r?.identifier?.SnsTopicARN);
export const HasCustomEventSelectors = r =>
  MetaField('Has custom event selectors', r?.config?.HasCustomEventSelectors);
export const HasInsightSelectors = r =>
  MetaField('Has insight selectors', r?.config?.HasInsightSelectors);
export const IsOrganizationTrail = r =>
  MetaField('Is organization trail', r?.config?.IsOrganizationTrail);
export const LatestNotificationAt = r =>
  MetaField('Latest notification at', r?.config?.LatestNotificationTime);
export const IsLogging = r => MetaField('Is logging', r?.config?.IsLogging);
export const StartLoggingTime = r =>
  MetaField('Start logging time', r?.config?.StartLoggingTime);
export const StopLoggingTime = r =>
  MetaField('Stop logging time', r?.config?.StopLoggingTime);
export const S3Bucket = r =>
  MetaField('S3 bucket', r?.identifier?.S3BucketName, {
    linkResourceType: NativeResources.SimpleStorageService,
  });
export const IncludeGlobalServiceEvents = r =>
  MetaField(
    'Include global service events',
    r?.config?.IncludeGlobalServiceEvents,
  );

export function MetaField(
  field,
  value,
  props: Omit<MetadataGridFieldProps, 'label' | 'value'> = {},
) {
  // TODO: we can check if we are showing some value here and
  // automatically avoid showing that in show more section
  const fieldProps =
    isString(field) && field.trim().toUpperCase().endsWith('ARN')
      ? { ...props, colSpan: isEmpty(value) ? 1 : 2 }
      : props;

  return <MetaGridField label={field} value={value} {...fieldProps} />;
}
